import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Clear, HelpOutline } from '@mui/icons-material';
import CKEDITOR from '@workboard/ckeditor5-build-wobo';
import { Button, Checkbox, IconButton, Modal, ModalContent, ModalHeader, ModalWrapper, ThemeProvider } from '@workboard/wobo-ui';
import { UserItemRenderer } from '@workboard/ui-search';
import { uniq } from 'lodash';
import React, { createRef } from 'react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import CreateWsPopUp from '../../CreateWsPopUp.component';
import {
  autoResizeTextArea,
  reactLoader,
  removeReactLoader
} from '../../../utils/common.js';
import Customokr from '../../custom_fields/customobj/Customokr';
import { FieldTypeAttributes } from '../../custom_fields/utils/constants';
import EmojiContainer from '../../emojis/emojis';
import ImgIconComp from '../../imgcomp';
import { ErrorTextComp } from '../../inputs/fields.component';
import GoalServices from '../../../services/goal.services';
import { CoauthorButton, getCoauthorConfig } from './coauthor';
import PermissionComp from './key_results/permission.component';
import he from 'he';
import MUIIcon from '../../../styled-base/mui-icon/index';
import PillarGeneralIcon from '../../../vectors/strategy-pillar-general.svg';
import PillarTransformationIcon from '../../../vectors/strategy-pillar-transformation.svg';
import PillarCultureIcon from '../../../vectors/strategy-pillar-culture.svg';
import PillarCustomersIcon from '../../../vectors/strategy-pillar-customers.svg';
import PillarGrowthIcon from '../../../vectors/strategy-pillar-growth.svg';
import { DurationSelect } from './DurationSelect'
import { AddFinancialTargetsButton } from './AddFinancialTargetsButton';
import { buildCustomFieldsDataArray } from '../../custom_fields/utils/buildCustomFieldsDataArray';
import { prepareCustomDataForDisplay } from '../../custom_fields/utils/customFieldsDisplayUtils';

const pillarIconMap = {
  General: PillarGeneralIcon,
  Transformation: PillarTransformationIcon,
  Culture: PillarCultureIcon,
  Customers: PillarCustomersIcon,
  Growth: PillarGrowthIcon,
}

/** Max nesting depth when flattening KRs in save validation (avoids runaway on bad data). */
const MAX_METRIC_TREE_WALK_DEPTH = 100

const StyledModal = styled(Modal)`
  .MuiDialog-scrollPaper {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
  }
  .MuiDialog-container{
		height: 100% !important;
	}
  ${ModalWrapper} {
    width: 50%;
    height: 100%;
    min-width: 760px;
    margin: 0 !important;
  }
  ${ModalHeader} {
    padding: 32px 100px 12px 60px !important;
    h1 {
      font-size: 20px !important;
    }
    svg {
      width: 24px;
      height: 24px;
    }
  }
  ${ModalContent} {
    padding: 0px !important;
    height: calc( 100% - 190px );
    max-height: calc(100vh - 192px) !important;
    overflow: auto;
    p {
       line-height: 1.5;
       margin: 0;
    }
  }
  ${ModalContent} + div {
    margin-right: 75px !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
`;
const StyledCheckbox = styled(Checkbox)`
.browse-checkbox {
  width: 10px;
  height: 0px;
}
  .MuiButtonBase-root {
    margin-left: -1px;
		padding: 0px;
  }
  input{
    display: inline-block !important;
  }
  svg {
    width: 24px;
    height: 24px;
  }
`;
const StyledIconButton = styled(IconButton)`
  &.MuiButtonBase-root {
    margin-left: 5px;
    padding: 0;
    min-width: 16px;
    min-height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  width: 16px;
  height: 16px;
  svg {
    width: 14px;
    height: 14px;
    display: block;
  }
`;

const RemoveIconButton = styled((props) => (
  <IconButton {...props}>
    <Clear />
  </IconButton>
))`
  width: 21px;
  height: 21px;
  svg {
    margin-top: 1px;
    width: 18px;
    height: 18px;
  }
  &.MuiButtonBase-root {
    vertical-align: top;
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

const ObjectiveContainer = styled('div')({
  position: 'relative',
  [`& ${CoauthorButton}`]: {
    position: 'absolute',
    right: 12,
    bottom: 8
  }
})

/**
 * Helper class for merging custom field data from API responses with existing state data.
 * Preserves savedValue attributes while validating against acceptable values for dropdown/multi-select fields.
 *
 * @example
 * // Sample currentCustomData structure (from state):
 * {
 *   "0": {
 *     "id": "0",
 *     "name": "",
 *     "customFields": {
 *       "11": {
 *         "id": "11",
 *         "object_type": "5",
 *         "object_id": "8491",
 *         "name": "OKR custom field single dropdown",
 *         "field_type_id": "3",
 *         "cat_id": "0",
 *         "fieldValues": [
 *           { "custom_attribute_id": "11", "value": "option 2", "default_value": "0" },
 *           { "custom_attribute_id": "11", "value": "option 3", "default_value": "0" }
 *         ],
 *         "savedValue": {
 *           "value": ["option 2"],
 *           "savedObjId": ["9437"],
 *           "fieldType": "3"
 *         },
 *         "text": "option 2"
 *       }
 *     }
 *   },
 *   "1243": {
 *     "id": "1243",
 *     "name": "Cat1",
 *     "customFields": {
 *       "1": {
 *         "id": "1",
 *         "object_type": "5",
 *         "object_id": "8491",
 *         "name": "OKR custom fields",
 *         "field_type_id": "4",
 *         "cat_id": "1243",
 *         "fieldValues": [
 *           { "custom_attribute_id": "1", "value": "Blue", "default_value": "0" },
 *           { "custom_attribute_id": "1", "value": "Green", "default_value": "0" }
 *         ],
 *         "savedValue": {
 *           "value": ["Blue"],
 *           "savedObjId": ["9443"],
 *           "fieldType": "4"
 *         },
 *         "text": [{ "name": "Blue", "status": true }]
 *       }
 *     }
 *   }
 * }
 *
 * @example
 * // Sample newCustomData structure (from API response):
 * {
 *   "0": {
 *     "id": "0",
 *     "name": "",
 *     "customFields": {
 *       "11": {
 *         "id": "11",
 *         "object_type": "5",
 *         "object_id": "8491",
 *         "name": "OKR custom field single dropdown",
 *         "field_type_id": "3",
 *         "cat_id": null,
 *         "fieldValues": [
 *           { "custom_attribute_id": "11", "value": "option 2", "default_value": "0" },
 *           { "custom_attribute_id": "11", "value": "option 3", "default_value": "0" },
 *           { "custom_attribute_id": "11", "value": "option1", "default_value": "0" }
 *         ]
 *         // Note: savedValue and text are missing - these will be merged from currentCustomData
 *       }
 *     }
 *   }
 * }
 */
class CustomFieldMerger {
  /**
   * Builds a lookup map from current custom data state
   * @param {Object} currentCustomData - Current customData from state
   * @returns {Object} Map of fieldId -> field object
   */
  buildFieldMap(currentCustomData) {
    const buildMapKey = (fieldId) => String(fieldId);
    const fieldMap = {};

    Object.keys(currentCustomData).forEach(categoryKey => {
      const category = currentCustomData[categoryKey];
      if (category && category.customFields) {
        Object.values(category.customFields).forEach(field => {
          if (field.id) {
            const key = buildMapKey(field.id);
            if (!fieldMap[key] || field.savedValue) {
              fieldMap[key] = field;
            }
          }
        });
      }
    });

    return fieldMap;
  }

  /**
   * Identifies and logs fields that exist in current state but not in new data
   * @param {Object} currentFieldMap - Map of current fields
   * @param {Object} newCustomData - New customData from response
   */
  identifyLostFields(currentFieldMap, newCustomData) {
    const buildMapKey = (fieldId) => String(fieldId);
    const newFieldKeys = new Set();
    const lostFields = [];

    // Build set of field IDs from new data
    Object.keys(newCustomData).forEach(categoryKey => {
      const category = newCustomData[categoryKey];
      if (category && category.customFields) {
        Object.values(category.customFields).forEach(field => {
          if (field.id) {
            newFieldKeys.add(buildMapKey(field.id));
          }
        });
      }
    });

    // Find fields that are lost
    Object.keys(currentFieldMap).forEach(key => {
      if (!newFieldKeys.has(key)) {
        const lostField = currentFieldMap[key];
        lostFields.push({
          fieldId: lostField.id,
          fieldName: lostField.name,
          objectId: lostField.object_id,
          objectType: lostField.object_type,
          savedValue: lostField.savedValue
        });
      }
    });

    if (lostFields.length > 0) {
      console.warn('CUSTOM FIELDS MERGING: Custom fields lost during merge:', lostFields);
    }
  }

  /**
   * Validates saved values against acceptable values for dropdown/multi-select fields
   * @param {Array} savedValues - Array of saved values
   * @param {Array} acceptableValues - Array of acceptable values
   * @returns {Object} Object with validIndices, validValues, and invalidValues
   */
  validateFieldValues(savedValues, acceptableValues) {
    const validIndices = [];
    const validValues = [];
    const invalidValues = [];

    savedValues.forEach((val, index) => {
      // Empty string is a valid "no selection" state for dropdown/multi-select fields
      // Allow it to pass through even if not in acceptableValues
      const isEmptyString = val === '' || val === null || val === undefined;
      const isValid = isEmptyString || acceptableValues.includes(val);

      if (isValid) {
        validIndices.push(index);
        validValues.push(val);
      } else {
        invalidValues.push(val);
      }
    });

    return { validIndices, validValues, invalidValues };
  }

  /**
   * Builds the text property for a field based on its type
   * @param {Object} newField - The new field object
   * @param {Number} fieldTypeId - The field type ID
   * @param {Array} validValues - Array of valid values
   * @param {Object} matchingField - The matching field from current state
   * @param {Array} savedValues - Original saved values array
   * @param {Array} validIndices - Array of valid indices
   */
  buildTextProperty(newField, fieldTypeId, validValues, matchingField, savedValues, validIndices) {
    if (fieldTypeId === FieldTypeAttributes.ATTRIBUTE_MULTI_SELECT) {
      // Multi-select: build text array from valid values
      const fieldValueTemp = newField.fieldValues || [];
      const textArray = fieldValueTemp.map(fv => ({
        name: fv.value,
        status: validValues.includes(fv.value)
      }));
      newField.text = textArray;
    } else if (fieldTypeId === FieldTypeAttributes.ATTRIBUTE_DROPDOWN) {
      // Dropdown: text should be the selected value string
      newField.text = validValues[0] || '';
    } else {
      // For other field types, preserve original text if it exists
      if (matchingField.text !== undefined) {
        if (Array.isArray(matchingField.text)) {
          // text is an array, filter by valid indices
          if (matchingField.text.length === savedValues.length) {
            const validText = validIndices.map(index => matchingField.text[index]);
            newField.text = validText.length === 1 ? validText[0] : validText;
          } else {
            // Length mismatch, copy as-is (shouldn't happen but handle gracefully)
            newField.text = matchingField.text;
          }
        } else {
          // text is a single value, only copy if first value is valid
          if (validIndices.includes(0)) {
            newField.text = matchingField.text;
          }
        }
      }
    }
  }

  /**
   * Ensures savedValue.value is always an array for CustomField component compatibility
   * @param {Object} savedValue - The savedValue object
   * @returns {Object} Normalized savedValue with value as array
   */
  normalizeSavedValue(savedValue) {
    const normalized = { ...savedValue };
    if (normalized.value !== undefined) {
      normalized.value = Array.isArray(normalized.value)
        ? normalized.value
        : [normalized.value];
    }
    return normalized;
  }

  /**
   * Merges a validated field (dropdown/multi-select) with value validation
   * @param {Object} newField - The new field from response
   * @param {Object} matchingField - The matching field from current state
   * @param {Number} fieldTypeId - The field type ID
   * @param {Array} acceptableValues - Array of acceptable values
   */
  mergeValidatedField(newField, matchingField, fieldTypeId, acceptableValues) {
    const savedValues = Array.isArray(matchingField.savedValue.value)
      ? matchingField.savedValue.value
      : [matchingField.savedValue.value];

    // Validate values
    const { validIndices, validValues, invalidValues } = this.validateFieldValues(
      savedValues,
      acceptableValues
    );

    // Debug logging
    if (savedValues.length > 0) {
      console.log('CUSTOM FIELDS MERGING: Validating field values:', {
        fieldId: newField.id,
        fieldName: newField.name,
        savedValues: savedValues,
        acceptableValues: acceptableValues,
        validValues: validValues,
        invalidValues: invalidValues
      });
    }

    // Log invalid values
    if (invalidValues.length > 0) {
      console.warn('CUSTOM FIELDS MERGING: Invalid custom field values removed:', {
        fieldId: newField.id,
        fieldName: newField.name,
        objectId: newField.object_id,
        objectType: newField.object_type,
        invalidValues: invalidValues,
        acceptableValues: acceptableValues,
        originalSavedValue: matchingField.savedValue
      });
    }

    // Update savedValue with only valid values
    if (validValues.length > 0) {
      newField.savedValue = {
        ...matchingField.savedValue,
        value: validValues  // Always keep as array, even for single values
      };

      // Update savedObjId if it exists - filter by valid indices to maintain alignment
      if (Array.isArray(matchingField.savedValue.savedObjId) &&
          matchingField.savedValue.savedObjId.length === savedValues.length) {
        const validObjIds = validIndices.map(index => matchingField.savedValue.savedObjId[index]);
        if (validObjIds.length > 0) {
          // Always keep as array to match value array structure
          newField.savedValue.savedObjId = validObjIds;
        } else {
          // Remove savedObjId if no valid values remain
          delete newField.savedValue.savedObjId;
        }
      }

      // Build text property based on field type
      this.buildTextProperty(newField, fieldTypeId, validValues, matchingField, savedValues, validIndices);
    } else {
      // All values were invalid, don't copy savedValue
      console.warn('CUSTOM FIELDS MERGING: All custom field values were invalid, savedValue not preserved:', {
        fieldId: newField.id,
        fieldName: newField.name,
        objectId: newField.object_id,
        objectType: newField.object_type
      });
    }
  }

  /**
   * Merges a non-validated field (text, textarea, etc.) without value validation
   * @param {Object} newField - The new field from response
   * @param {Object} matchingField - The matching field from current state
   */
  mergeNonValidatedField(newField, matchingField) {
    // Ensure value is always an array for CustomField component compatibility
    const savedValueCopy = this.normalizeSavedValue(matchingField.savedValue);
    newField.savedValue = savedValueCopy;

    // Also copy text if it exists (for display purposes)
    if (matchingField.text !== undefined) {
      newField.text = matchingField.text;
    }
  }

  /**
   * Merges new custom field data from response with existing state data,
   * preserving savedValue attributes by matching field id
   * @param {Object} currentCustomData - Current customData from state
   * @param {Object} newCustomData - New customData from response
   * @returns {Object} Merged customData with preserved savedValue attributes
   */
  mergeCustomData(currentCustomData, newCustomData) {
    if (!currentCustomData || !newCustomData) {
      return newCustomData || currentCustomData || {};
    }

    // Deep clone the new data to avoid mutating the original
    const merged = JSON.parse(JSON.stringify(newCustomData));

    // Build lookup map from current state
    const currentFieldMap = this.buildFieldMap(currentCustomData);

    // Identify and log lost fields
    this.identifyLostFields(currentFieldMap, merged);

    // Lambda function for building map key
    const buildMapKey = (fieldId) => String(fieldId);

    // Iterate through categories in new data and merge savedValue
    Object.keys(merged).forEach(categoryKey => {
      const category = merged[categoryKey];
      if (category && category.customFields) {
        // Iterate through customFields in the new category
        Object.keys(category.customFields).forEach(fieldKey => {
          const newField = category.customFields[fieldKey];

          // Find matching field in current state by field id
          if (newField.id) {
            const lookupKey = buildMapKey(newField.id);
            const matchingField = currentFieldMap[lookupKey];

            // If match found and it has savedValue, copy it over
            if (matchingField && matchingField.savedValue) {
              const fieldTypeId = parseInt(newField.field_type_id, 10);
              const isDropdownOrMultiSelect = fieldTypeId === FieldTypeAttributes.ATTRIBUTE_DROPDOWN ||
                                              fieldTypeId === FieldTypeAttributes.ATTRIBUTE_MULTI_SELECT;

              // For dropdown and multi-select fields with acceptable values, validate savedValue
              if (isDropdownOrMultiSelect &&
                  newField.fieldValues &&
                  Array.isArray(newField.fieldValues) &&
                  newField.fieldValues.length > 0) {
                const acceptableValues = newField.fieldValues.map(fv => fv.value);
                this.mergeValidatedField(newField, matchingField, fieldTypeId, acceptableValues);
              } else {
                // No fieldValues restriction, copy savedValue as-is
                this.mergeNonValidatedField(newField, matchingField);
              }
            }
          }
        });
      }
    });

    return merged;
  }
}

class EditGoal extends React.Component {
  constructor(props) {
    super(props);
    this.scollToRef = createRef();
    this.workstremRef= createRef();

    const hasAdditionalData = (
      (this.props.goalDetails.dependent_ws !== undefined &&
        this.props.goalDetails.dependent_ws.length > 0) ||
        (this.props.goalDetails.dependent_goals !== undefined &&
          this.props.goalDetails.dependent_goals.length > 0) ||
          (this.props.goalDetails.copied_from_id !== undefined &&
          this.props.goalDetails.copied_from_id !== '')
    );
    // eslint-disable-next-line react/destructuring-assignment
    const rawCustomData = this.props.goalDetails.groupCustomFields ?
      // eslint-disable-next-line react/destructuring-assignment
      this.props.goalDetails.groupCustomFields :
      // eslint-disable-next-line react/destructuring-assignment
      this.props.addOkrData.groupOKRCustomFields || {};
    const customData = prepareCustomDataForDisplay(rawCustomData);

    this.state = {
      isRequirdCustomOkr:[],
      customInputData:[],
      isCustomSave:[],
      teamOptions: [],
      filter: {
        Team: [],
        Owner: [],
        ObjectiveAlign: [],
        Dependecies: [],
        Categories: [],
        WorkStream: [],
        okrAlignmentSuggestions: []
      },

      ObjectiveAlingSelected:
        this.props.goalDetails &&
          this.props.goalDetails.goal_aligns !== undefined &&
          this.props.goalDetails.goal_aligns.length > 0
          ? this.props.goalDetails.goal_aligns
          : [],
      dependenciesSelected:
        this.props.goalDetails &&
          this.props.goalDetails.dependent_goals !== undefined &&
          this.props.goalDetails.dependent_goals.length > 0
          ? this.props.goalDetails.dependent_goals
          : [],
      categorySelected:
        this.props.goalDetails &&
          this.props.goalDetails.goal_categories !== undefined &&
          this.props.goalDetails.goal_categories.length > 0
          ? this.props.goalDetails.goal_categories
          : [],
      workStreamSelected:
        this.props.goalDetails &&
          this.props.goalDetails.dependent_ws !== undefined &&
          this.props.goalDetails.dependent_ws.length > 0
          ? this.props.goalDetails.dependent_ws
          : [],
      ownerDataSpinner: false,
      ownerList: this.props.ownerList,
      editGoalName: this.props.goalDetails.goal,
      start_date: this.props.goalDetails.start_date,
      end_date: this.props.goalDetails.target_date,
      owner_name: this.props.goalDetails.owner_fullname,
      team_name: this.props.goalDetails.team_name,
      owner_id: this.props.goalDetails.owner_id,
      team_id: this.props.goalDetails.team_id,
      narrative: this.props.goalDetails.narrative
        ? this.props.goalDetails.narrative
        : '',
      teamPermissionSelected:
        this.props.goalDetails && this.props.goalDetails.permission_teams
          ? this.props.goalDetails.permission_teams
          : [],
      bizPermissionSelected: this.props.goalDetails && this.props.goalDetails.permission_biz
      ? this.props.goalDetails.permission_biz
      : [],
      dropdownOpen: 'close',
      searchSet: false,
      teamGoal: this.props.goalDetails.type == 1 ? true : false,
      createWsPopUp: false,
      wsArchiEn:
      hasAdditionalData
          ? true
          : this.props.divWsArchiEn !== true
            ? true
            : false,
      divWsArchiEn:
      hasAdditionalData
          ? false
          : this.props.divWsArchiEn,
      teamEn: false,
      ownerEn: false,
      addOptionalDesc:
        this.props.goalDetails && this.props.goalDetails.narrative
          ? true
          : false,
      alignmentEn: false,
      isChecked: false,
      achievingEn: false,
      dependenciedEn: false,
      pillarsEn: false,
      categoryEn: false,
      permissonEn: false,
      addGoalValue: this.props.addGoalValue,
      rightokrpane: ' openLeftPane',
      subPermissonEn: false,
      errors: {},
      alignsHelpPopUp: false,
      durationHelpPopUp: false,
      permissionHelpPopUp: false,
      categoryHelpPopUp: false,
      depedenciesHelpPopUp: false,
      achiHelpPopUp: false,
      krOwnerPic: this.props.goalDetails.owner_pic,
      isPermissionChecked: true,
      permission: [],
      permissionstate:
        this.props.goalDetails && this.props.goalDetails.permission.length > 0
          ? this.props.goalDetails.permission.split(',').map(Number)
          : this.props.goalDetails.type == 1
            ? [8]
            : [10],
      Exclude:
        this.props.goalDetails && this.props.goalDetails.all_metrics == 1
          ? true
          : false,
      OldExclude:
        this.props.goalDetails && this.props.goalDetails.all_metrics == 1
          ? true
          : false,
      krPermissionText:
        this.props.goalDetails && this.props.goalDetails.permission.length > 0
          ? this.props.goalDetails &&
            this.props.goalDetails.permission.length > 1
            ? 'Custom view permissions'
            : 'Organization can view'
          : this.props.goalDetails.type == 1
            ? 'Only team can view'
            : 'Only owner can view',
      _isOrgTeamMounted: false,
      _isObjectiveAlignmentMounted: false,
      _isOwnerMounted: false,
      _isDependecyMounted: false,
      _isCategoriesMounted: false,
      _isWorkStreamMounted: false,
      hide: true,
      teamSearchVal: '',
      ownerSearchVal: '',
      objAlignSearchVal: '',
      categorySearchVal: '',
      dependencySearchVal: '',
      workstreamSearchVal: '',
      pillarsSearchVal: '',
      continuitySearchVal: '',
      setreverseCat: '',
      setreverseDep: '',
      setreverseWs: '',
      setreversePillars: '',
      setreverseContinuity: '',
      limit: 5,
      offset: 5,
      permissionOldstate:
        this.props.goalDetails && this.props.goalDetails.permission.length > 0
          ? this.props.goalDetails.permission.split(',').map(Number)
          : this.props.goalDetails.type == 1
            ? [8]
            : [10],
      all_metricsOldState:
        this.props.goalDetails && this.props.goalDetails.all_metrics == 1
          ? true
          : false,
      selectedOption: this.props.goalDetails.start_date && this.props.goalDetails.target_date ? {
        start: typeof this.props.goalDetails.start_date === 'number' ?
          moment.unix(this.props.goalDetails.start_date).utc().format('MM/DD/YYYY') :
          moment.utc(this.props.goalDetails.start_date).format('MM/DD/YYYY'),
        end: typeof this.props.goalDetails.target_date === 'number' ?
          moment.unix(this.props.goalDetails.target_date).utc().format('MM/DD/YYYY') :
          moment.utc(this.props.goalDetails.target_date).format('MM/DD/YYYY'),
      } : undefined,
      filterDateArray: Object.values(this.props.goalDetails.filterOption),
      isNested: false,
      ck_editor: null,
      enAlignSuggestion: false,
      okrAlignmentSuggestions: [],
      okrAlignsEvents: [],
      categoryRequired:false,
      customFiledRequired:false,
      categoryTrueForPersonal:this.props && this.props.addOkrData &&
                  this.props.addOkrData.objective_category_required.enabled==1 &&
                  this.props.addOkrData.objective_category_required.values.includes(2)
                      ? true
                      : false,
      categoryTrueForTeamObjective:this.props && this.props.addOkrData &&
                   this.props.addOkrData.objective_category_required.enabled==1 &&
                   this.props.addOkrData.objective_category_required.values.includes(1)
                      ? true
                      : false,
      customData,
      customFieldsData: buildCustomFieldsDataArray(customData, '5', true),
      continuityHelpPopUp: false,
      continuityEn: false,
      continuityArchiEn:  (this.props.goalDetails.dependent_ws !== undefined &&
        this.props.goalDetails.dependent_ws.length > 0) ||
        (this.props.goalDetails.dependent_goals !== undefined &&
          this.props.goalDetails.dependent_goals.length > 0) ||
          (this.props.goalDetails.copied_from_id !== undefined &&
          this.props.goalDetails.copied_from_id !== '') ? true : false,
      continuitySelected:
        this.props.goalDetails &&
          this.props.goalDetails.copied_from_id !== undefined
          ? this.props.goalDetails.copied_from_id
          : '',
      pillarsArchiEn: wbConfig?.isPillarAlignmentEnabled && hasAdditionalData,
      pillarsSelected: [],
      pillarsExisting: [],
      pillarsDeleted: [],
      strategyBaseUrl: '/'
    };

    this.setRightPaneValue = this.setRightPaneValue.bind(this);
    this.saveGoal = this.saveGoal.bind(this);
    this.closeWsPopUp = this.closeWsPopUp.bind(this);
    this.handleExcludeChange = this.handleExcludeChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.passDropDownParams = this.passDropDownParams.bind(this);
    this.select = this.select.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.GoalServices = new GoalServices();
    this.handleGoalNarrativeChange = this.handleGoalNarrativeChange.bind(this);
    this.displayWorkDependent = this.displayWorkDependent.bind(this);
    this.toggelePermission = this.toggelePermission.bind(this);
    this.toggeleSubPermission = this.toggeleSubPermission.bind(this);
    this.onEnterPress = this.onEnterPress.bind(this);
    this.renderError = this.renderError.bind(this);
    this.moveCaretAtEnd = this.moveCaretAtEnd.bind(this);
    this.permissionData = this.permissionData.bind(this);
    this.teamPermissionData = this.teamPermissionData.bind(this);
    this.isRequiredCustomOkrFiled = this.isRequiredCustomOkrFiled.bind(this);
    this.isSaveCustom =this.isSaveCustom.bind(this)
    this.changeSearchBox = this.changeSearchBox.bind(this);
    this.enableDepdency = this.enableDepdency.bind(this);
    this.compare = this.compare.bind(this);
    this.onChangeFilters = this.onChangeFilters.bind(this);
    this.editObjhelpPopperHandler = this.editObjhelpPopperHandler.bind(this);
    this.editObjhelpPopperTabHandler = this.editObjhelpPopperTabHandler.bind(this);
    this.enableAlignSuggestion = this.enableAlignSuggestion.bind(this);
    this.okrAlignKeyDown=this.okrAlignKeyDown.bind(this);
    this.observer = createRef(null);
    this.onBlurEvent = this.onBlurEvent.bind(this);
    this.captureOldName = this.props.goalDetails.goal;
    this.coauthorNode = React.createRef();
    this.enableContinuity = this.enableContinuity.bind(this);
    this.enablePillars = this.enablePillars.bind(this);

    this.initPillars();
    this.initCoauthorOptions();
    this.customFieldMerger = new CustomFieldMerger();
  }

  initPillars = async () => {
    const goalId = this.props.goalDetails.id;

    if(!goalId || !wbConfig?.isPillarAlignmentEnabled) return;

    const result = await this.GoalServices.getPillarsOnObjectives([goalId]);

    const pillars = result.data?.data?.[goalId]?.map((p) => ({
      id: p?.pillarId,
      ...p,
    }));

    if(!pillars?.length) return;

    this.setState({
      pillarsExisting: pillars,
      pillarsSelected: pillars,
      pillarsDeleted: [],
      pillarsArchiEn: true,
      // set all additional fields visible
      wsArchiEn: true,
      continuityArchiEn: true,
      divWsArchiEn: false,
      strategyBaseUrl: result.data?.strategyUrl
    });
  }

  initCoauthorOptions = async () => {
    const teamOptions = await this.GoalServices.searchTeamName({
      term: '',
      offset: 0,
      limit: 100
    }).then(response => response.data ?? []);

    this.setState({ teamOptions });
  }

  delayedCalleback = (event, term) => {
    if (term == 'team') {
      this.setState({ teamSearchVal: event.target.value }, () => this.filterTeamFunction(event));
    } else if (term == 'owner') {
      this.setState({ ownerSearchVal: event.target.value }, () => this.filterOwnerFunction(event));
    } else if (term == 'objAlign') {
      this.setState({ objAlignSearchVal: event.target.value }, () => this.filterObjectiveAlignFunction(event));
    } else if (term == 'category') {
      this.setState({ categorySearchVal: event.target.value }, () => this.filterCategoriesFunction(event));
    } else if (term == 'dependency') {
      this.setState({ dependencySearchVal: event.target.value }, () => this.filterDependenciesFunction(event));
    } else if (term == 'workstream') {
      this.setState({ workstreamSearchVal: event.target.value }, () => this.filterWorkStreamFunction(event));
    } else if (term == 'continuity') {
      this.setState({ continuitySearchVal: event.target.value }, () => this.filterContinuityFunction(event));
    } else if (term == 'pillars') {
      this.setState({ pillarsSearchVal: event.target.value }, () => this.filterPillarsFunction(event));
    }
  };

  componentWillReceiveProps(nextProps) {
    if(this.state.okrAlignmentSuggestions?.length <=0 ){
      this.setState({okrAlignmentSuggestions: nextProps.okrAlignmentSuggestionsDate})
    }

    if(nextProps.goalDetails?.metrics?.find(m => m?.nested_krs?.length >= 1) ||
      (nextProps.goalDetails?.metrics?.find(m => m?.child_krs?.length >= 1) &&
        Math.ceil(moment(nextProps.goalDetails.target_date).diff(moment(nextProps.goalDetails.start_date), 'days', true)) >= 360)) {
      this.setState({ isNested : true})
    }
  }
  changeSearchBox(event, term) {
    // document.querySelector('#wrkstrmMy').scrollIntoView()
    // this.workstremRef.current.scrollIntoView()

    event.persist();
    this.delayedCalleback(event, term);
  }

  fetchData(node){
    let _this = this;
    if (this.observer.current) this.observer.current.disconnect()
    this.observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        if(_this.state.limit === 5 && (!_this.state.teamSearchVal ||  _this.state.teamSearchVal.length <= 0 )){
          _this.GoalServices.searchTeamName({
            term: '',
            offset: _this.state.offset,
            limit: _this.state.limit
          }).then(function (response) {
            if (response.data) {
              let team =  [..._this.state.filter.Team, ...response.data];
              _this.setState({
                filter: {
                  ..._this.state.filter,
                  Team: uniq(team,'id')
                },
                offset: _this.state.offset + 5,
                limit: response.data.length
              });
            }
          });
        }
      }
    })
    if (node) this.observer.current.observe(node)
  }

  fetchObjCat(node){
    let _this = this;
    if (this.observer.current) this.observer.current.disconnect()
    this.observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        if(_this.state.limit === 5 && (!_this.state.teamSearchVal ||  _this.state.teamSearchVal.length <= 0 )){
          _this.GoalServices.searchObjectiveCategory({
            term: '',
            offset: _this.state.offset,
            limit: _this.state.limit
          }).then(function (response) {
            if (response.data) {
              let cat =  [..._this.state.filter.Categories, ...response.data];
              _this.setState({
                filter: {
                  ..._this.state.filter,
                  Categories: uniq(cat,'group_category_id')
                },
                offset: _this.state.offset + 5,
                limit: response.data.length
              });
            }
          });
        }
      }
    })
    if (node) this.observer.current.observe(node)
  }

  handleKey(event,term){
    if (event.keyCode === 13 && term == 'team') {
      this.setState({ teamEn: !this.state.teamEn });
      this.changeSearchBox(event, 'team');
    }
    if (event.keyCode === 13 && term == 'teamSelect') {
      this.setState({ teamEn: !this.state.teamEn });
      this.changeSearchBox(event, 'team');
    }
    if (event.keyCode === 13 && term == 'category') {
      this.closeCategory(event);
      this.changeSearchBox(event, 'category');
    }
  }

  fetchDataProp = this.fetchData.bind(this);
  fetchObjCatProp = this.fetchObjCat.bind(this);

  handleKeyWithData(event,term,valTeam){
    if (event.keyCode === 13 && term == 'teamSelect') {
      const focusEl = document.getElementById('teamsDropdown')
      this.select(event, valTeam);
      focusEl.focus()
    }
  }

  enableAlignSuggestion() {
    this.setState({
      enAlignSuggestion: true,
    })
    if (this.state.okrAlignmentSuggestions.length > 0) {
      let okrIds = this.state.okrAlignmentSuggestions.map((x)=>x.id)
      this.GoalServices.nlpEventTrack({ref:'NLP_OKR_ALIGNMENT_VIEW',okrId:okrIds})
    }
  }

  handleExcludeChange() {
    this.setState({
      Exclude: !this.state.Exclude
    });
  }

  handleStartDateChange(selectedDay, modifiers, dayPickerInput) {
    let dateObj = {};
    dateObj.start_date = selectedDay;
    if (
      typeof dateObj.start_date !== 'undefined' &&
      this.state.end_date &&
      moment(dateObj.start_date).diff(moment(this.state.end_date)) >= 0
    ) {
      dateObj.end_date = '';
    }
    this.setState(dateObj);
  }

  handleEndDateChange(selectedDay, modifiers, dayPickerInput) {
    let end_date = selectedDay;
    this.setState({ end_date });
  }

  componentDidMount() {
    this.props.disableEscFunc();
    if (this.props.divWsArchiEn !== true) {
      document.querySelector('#wrkstrmMy').scrollIntoView()
      if($('.objectiveAchi').length > 0){
        let offSet = $('.objectiveAchi').offset().top;
        $('.aEGoalContent').parent().animate({ scrollTop: offSet });
      }
    }
    document.addEventListener('mousedown', this.handleClickOutside);
    if (this.goalEditTextArea) {
      this.goalEditTextArea.focus();
      autoResizeTextArea(this.goalEditTextArea);
    }
    overflowHiddenFunction(true);
    let windowHeight = $(window).height();
    $('#mySidenav .aEGoalContent').css({
      'min-height': windowHeight - 244,
      'max-height': windowHeight - 244
    });

    window.addEventListener('coauthorSaveObjective', this.handleCoauthorSaveObjective);
    window.addEventListener('coauthorConfigChange', this.handleCoauthorConfigChange);
  }

  handleCoauthorSaveObjective = (e) => {
    const { value, originatorId } = e.detail;
    if (originatorId !== 'addGoalValueTxt') return;
    const textarea = document.getElementById('addGoalValueTxt');
    textarea.value = value;
    this.handleGoalNameChange({ target: { value }})
  }

  handleCoauthorConfigChange = (e) => {
    const config = e.detail;

    this.setState({
      team_id: config.team?.id,
      team_name: config.team?.name,
      ...(config.timePeriod ?
      {
        start_date: config.timePeriod?.start,
        target_date: config.timePeriod?.end
      } : {})
    });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    window.removeEventListener('coauthorSaveObjective', this.handleCoauthorSaveObjective);
    window.removeEventListener('coauthorConfigChange', this.handleCoauthorConfigChange);
  }

  passDropDownParams(valArray) {
    return (
      <div>
        {valArray.map(val => {
          <span key={val.id}>{val.memberFullName[0]}</span>;
        })}
      </div>
    );
  }
isSaveCustom(isVal){
  let arr=[];
  let j=0;
  let keyArrCategory=[]
  let valArray = []
  isVal && Object.keys(isVal).forEach((v)=>{
    j++;
    isVal[v]['customFields']&&
    Object.keys(isVal[v]['customFields']).forEach(objv=>{
      keyArrCategory.push(objv)
      if(isVal[v]['customFields'][objv]['is_required']=='1'
      &&( isVal[v]['customFields'][objv]['text'] == undefined
        || isVal[v]['customFields'][objv]['text'] === ''
        || (Array.isArray(isVal[v]['customFields'][objv]['text']) && isVal[v]['customFields'][objv]['text'].length === 0))
      ){
           arr.push(objv,v)
          }
      if(isVal[v]['customFields'][objv]['text']){
        valArray.push(isVal[v]['customFields'][objv]['text'])
      }else{
        valArray.push('')
          }
    })
    if(j==Object.keys(isVal).length){
      this.setState({isRequirdCustomOkr:[...arr]})
    }
    let finalDataArray=[]
    for(let i=0;i<valArray.length;i++){
      if(Array.isArray(valArray[i])){
        valArray[i].forEach(val => {
          finalDataArray.push({id:keyArrCategory[i],val:val.name,object_type:'5'})
        })
      }
      else{
        finalDataArray.push({id:keyArrCategory[i],val:valArray[i],object_type:'5'})
    }

    }
    const tempData=finalDataArray
  })

}

  isRequiredCustomOkrFiled(isVal,id){
    let cust_id=this.state.isCustomSave.find((u) => u == id);
    let ar=this.state.isCustomSave ? this.state.isCustomSave:'';
    let fr= ar.filter(
        (u) => u != cust_id
      )
    this.setState({isCustomSave:fr})
    this.isSaveCustom(isVal)
    this.setState({customInputData:isVal})
  }
  getOkrDuration()
  {
    const { selectedOption } = this.state;
    const start_date = selectedOption?.start;
    const end_date = selectedOption?.end;
    return {start_date, end_date, selectedDate: selectedOption};
  }

  handleCoauthorObjectiveClick = async (e) => {
    const textarea = document.getElementById('addGoalValueTxt');
    const initialDraft = textarea.value ?? '';
    window.openOkrCoauthor({
      type: 'OPEN_OBJECTIVE_CHAT',
      initialDraft,
      anchor: textarea,
      placement: 'bottom',
      config: {
        ...getCoauthorConfig({
          team_id: this.state.team_id,
          team_name: this.state.team_name,
          filterOption: this.props.goalDetails.filterOption,
          start_date: this.state.selectedOption?.start,
          target_date: this.state.selectedOption?.end
        }, this.props.goalType),
        showPromptForTeamAndTimePeriod: true
      },
      originator: { id: 'addGoalValueTxt', type: 'objective' },
      initialDraftOKR: {
        objective: initialDraft,
        keyResults: []
      },
      width: textarea.clientWidth,
      teamOptions: this.state.teamOptions,
      timePeriodOptions: this.state.filterDateArray
    }, this.coauthorNode.current);
  }

  saveGoal() {
    const { t } = this.props;
    let errors = {};
    let goalDetails = this.props.goalDetails;
    const {editGoalName, okrAlignsEvents,
      owner_id, team_id, narrative, ObjectiveAlingSelected = [],
      dependenciesSelected = [], categorySelected = [],
      workStreamSelected = [], teamPermissionSelected = [],
      bizPermissionSelected = [], continuitySelected = '',
    } = this.state;
    let goalName = editGoalName.trim();
    const { start_date, end_date } = this.getOkrDuration();
    let _this = this;
    this.isSaveCustom(this.state.customInputData)
    this.setState({isCustomSave:[]})
    if(this.props.addOkrData.category_exist && categorySelected.length<1 &&
      ((this.state.categoryTrueForPersonal && this.props.addOkrData.goalDetails.type==2)
        ||(this.state.categoryTrueForTeamObjective && this.props.addOkrData.goalDetails.type==1))){
      errors['categorySelected'] = t('Please select the objective category');
    }
    if (goalName == '' || goalName == null) {
      errors['editGoalName'] = t('Objective name required');
    }
    if(this.state.isRequirdCustomOkr && this.state.isRequirdCustomOkr.length>0 ){
      errors['customokr'] = t('Please enter required field');
      this.setState({isCustomSave:this.state.isRequirdCustomOkr})
      setTimeout(() => {
        this.setState({customFiledRequired:true})
        document.querySelector('#cutomobj')?.scrollIntoView({
          behavior: 'smooth',
        })
      }, 200)

    }
    if (start_date == '' || start_date == null) {
      errors['start_date'] = t('Please select start date.');
    }
    if (end_date == '' || end_date == null) {
      errors['end_date'] = t('Please select target date.');
    }

    if (start_date && !moment(start_date).isValid()) {
      errors['start_date'] = t('Invalid start date.');
    }

    if (end_date && !moment(end_date).isValid()) {
      errors['end_date'] = t('Invalid end date.');
    }

    if (moment(start_date).diff(moment(end_date)) >= 0) {
      errors['end_date'] = t('End date should be greater than start date.');
    }

    if (goalDetails.type == '1' && this.state.team_name == '') {
      errors['team_name'] = t('Please select the team.');
    }
    // Validate owner is in the valid owner list (skip validation for new goals)
    if (owner_id && this.state.ownerList && this.state.ownerList.length > 0 &&
        this.props.addOkrObject.state.goalOperation != 'new') {
      const isValidOwner = this.state.ownerList.some(owner => owner.userId == owner_id);
      if (!isValidOwner) {
        const ownerName = this.state.owner_name || 'Unknown User';
        errors['owner_validation'] = `${ownerName} has Read Only access and can't be assigned OKRs`;
      }
    }
    if (!owner_id) {
      errors['owner_validation'] = t('Please select the owner.');
    }

    // Validate that key results don't exist outside objective date range
    if (start_date && end_date && moment(start_date).isValid() && moment(end_date).isValid()) {

      const objStartDate = this.parseDate(start_date);
      const objEndDate = this.parseDate(end_date);

      const allMetricsAtAnyDepth = this.flattenGoalMetrics(goalDetails);

      const hasKrOutsideRange = allMetricsAtAnyDepth.some((metric) => {
        if (!metric.start_date || !metric.end_date) return false;
        const krStart = this.parseDate(metric.start_date);
        const krEnd = this.parseDate(metric.end_date);
        return krStart.isValid() && krEnd.isValid() &&
               (krStart.isBefore(objStartDate) || krEnd.isAfter(objEndDate));
      });

      if (hasKrOutsideRange) {
        errors['kr_date_range'] = t('Key Results exist outside Objective Range');
      }

      // Block objective date change when any KR at any depth has a start or end date set
      const formatForMatch = (date) => {
        if (!date) return '';
        if (typeof date === 'number') return moment.unix(date).utc().format('MM/DD/YYYY');
        return moment.utc(date).format('MM/DD/YYYY');
      };
      const datesChanged = goalDetails.start_date && goalDetails.target_date &&
        (formatForMatch(start_date) !== formatForMatch(goalDetails.start_date) ||
         formatForMatch(end_date) !== formatForMatch(goalDetails.target_date));
      const hasCustomDatesInTree = allMetricsAtAnyDepth.some((m) =>
        this.metricHasAnyPopulatedDate(m)
      );
      if (
        (hasCustomDatesInTree || goalDetails.key_results_have_custom_dates_fy_objective) &&
        datesChanged
      ) {
        errors['kr_date_range'] = t('Key Results have custom dates');
      }
    }

    if (Object.keys(errors).length) {
      this.setState({ errors });
      if (errors) {
        if (errors.editGoalName && errors.editGoalName.length > 0) {
          $('.aEGoalContent').parent().animate({ scrollTop: 0 });
        } else if (
          (errors.start_date && errors.start_date.length > 0) ||
          (errors.end_date && errors.end_date.length > 0)
        ) {
          $('.aEGoalContent').parent().animate({ scrollTop: 100 });
        }else if(errors.categorySelected && categorySelected<1 &&
          ((this.state.categoryTrueForPersonal && this.props.addOkrData.goalDetails.type==2)
             ||(this.state.categoryTrueForTeamObjective && this.props.addOkrData.goalDetails.type==1))){ // more conditions will be added here which will come from backend
          this.scollToRef.current.scrollIntoView()
          this.setState({categoryRequired:true})
          setTimeout(() => {
            document.getElementById('objCategory').focus()
            document.getElementById('objCategory').setAttribute('aria-invalid', 'true');
          }, 200);

        }


      }
    } else {
      let params = {};
      params.goal = he.encode(goalName);
      params.id = goalDetails.id;
      params.team_id = team_id;
      params.owner_id = owner_id;
      params.start_date = start_date;
      params.target_date = end_date;
      params.goal_aligns = (Object.keys(ObjectiveAlingSelected).length > 0) ? ObjectiveAlingSelected.map((item) => item.id) : [];
      params.dependent_goals = (Object.keys(dependenciesSelected).length > 0) ? dependenciesSelected.map((item) => item.id) : [];
      params.copied_from_id = Object.keys(continuitySelected) ? continuitySelected.id : '';
      params.dependent_ws = (Object.keys(workStreamSelected).length > 0)
        ? workStreamSelected.filter((v) => Object.keys(v).length).reduce((i, v) => {
          if (typeof v.ws !== 'undefined' && v.ws.length) {
              return i.concat(...v.ws.map((item) => item.id))
      }}, []) : [];
      params.goal_categories = (Object.keys(categorySelected).length > 0)
        ? categorySelected.filter((v) => Object.keys(v).length).reduce((i, v) => {
          if (typeof v.sub_categories !== 'undefined' && v.sub_categories.length) {
              return i.concat(...v.sub_categories.map((item) => item.id))
      }}, []) : [];
      params.narrative = narrative;
      params.type = goalDetails.type;
      params.all_metrics = this.state.Exclude ? 1 : 0;
      params.okrAlignsEvents = okrAlignsEvents;
      params.permission_teams = teamPermissionSelected.length > 0 ? teamPermissionSelected.map((val) => val.id) : [];
      params.permission_biz = bizPermissionSelected.length > 0 ? bizPermissionSelected.map((val) => val.id) : [];
      params.customField= JSON.stringify(this.state.customFieldsData);
      let comparePer = false;
      if (
        goalDetails.type == 1 &&
        this.state.permissionOldstate.length > 0 &&
        this.state.permissionstate.length > 0
      ) {
        comparePer = this.compare(
          this.state.permissionOldstate,
          this.state.permissionstate
        );
      }
      if (
        goalDetails.type == 2 &&
        this.state.permissionOldstate.length > 0 &&
        this.state.permissionstate.length > 0
      ) {
        comparePer = this.compare(
          this.state.permissionOldstate,
          this.state.permissionstate
        );
      } else if (
        goalDetails.type == 2 &&
        this.state.permissionOldstate.length > 0 &&
        this.state.permissionstate.length < 1
      ) {
        comparePer = true;
      } else if (
        goalDetails.type == 2 &&
        this.state.permissionOldstate.length < 1 &&
        this.state.permissionstate.length > 0
      ) {
        comparePer = true;
      } else if (
        goalDetails.type == 2 &&
        this.state.permissionOldstate.length < 1 &&
        this.state.permissionstate.length < 1
      ) {
        comparePer = true;
      }
      if (this.state.all_metricsOldState != params.all_metrics && params.all_metrics == 1) {
        comparePer = true;
      }
      params.permissionChanged = comparePer;
      let perData = Array.prototype.map
        .call(this.state.permissionstate, s => s)
        .toString(); // "A,B,C";
      if (perData == 0 && goalDetails.type == '1') {
        params.permission = 7;
      } else {
        params.permission = perData;
      }
      let { actions } = this.props;
      let goalId = this.props.goalDetails ? this.props.goalDetails.id : null;
      reactLoader(true);
      this.GoalServices.addEditObjective({ goal: params }).then(async (response) => {
        if(response?.data) {

        let updateMetrics = false;

        const updatedMetrics = [];

        // Null out metric dates that match the new date selected for goal on frontend
        (goalDetails.metrics || []).forEach(metric => {
          if (metric.start_date && metric.end_date) {
            const krStart = this.parseDate(metric.start_date);
            const krEnd = this.parseDate(metric.end_date);
            const { start_date, end_date } = this.getOkrDuration();
            if (krStart.isValid() && krEnd.isValid() &&
                krStart.isSame(this.parseDate(start_date)) && krEnd.isSame(this.parseDate(end_date))) {
              updateMetrics = true;
              updatedMetrics.push(metric);
            }
          }
        });

        // Update metrics via API if any were modified
        if (updateMetrics && updatedMetrics.length > 0 && goalDetails.id) {
          const goalId = goalDetails.id;
          updatedMetrics.forEach(metric => {
            const params = {
              id: metric.id,
              startDate: null,
              targetDate: null,
              clearDates: true
            };
            this.GoalServices.addEditKr({ goalId: goalId, metric: params }).catch(error => {
              console.error('Error updating metric dates:', error);
            });
          });
        }

          await new Promise((resolve) => {
            // give the Objective some time to be created and propagate before aligning pillar
            setTimeout(() => {
              Promise.all([
                this.state.pillarsSelected?.length ?
                  this.GoalServices.savePillarObjectiveAlignment({
                    pillarIds: this.state.pillarsSelected.map((p) => p.id),
                    objectiveId: response?.data?.data?.id
                  })
                  : null,
                this.state.pillarsDeleted?.length ?
                  Promise.all(this.state.pillarsDeleted.map((p) =>
                    this.GoalServices.deletePillarObjectiveAlignment({ pillarId: p.id, objectiveId: response?.data?.data?.id })))
                  : null,
              ]).finally(resolve)
            }, goalId ? 0 : 3000) // dont delay when Objective already exists
          })
        }
        removeReactLoader();
        if (response.data) {
          if (typeof goalId !== 'undefined' && goalId != null) {
            _this.props.addOkrObject.setState({ goalOperation: 'edit' });
            // this.GoalServices.triggerGoalMetricDetails({ goalId });
          } else {
            _this.props.addOkrObject.setState({ goalOperation: 'new' });
          }
          response.data.data.goal = he.decode(response.data.data.goal)
          actions.updateEditedOkr(response.data);
          if (this.state.Exclude == 1) {
            actions.updatePermissionsInMetric(
              params.permission
            );
          }

          const channel = new BroadcastChannel('okr:objectiveCreated')
          channel.postMessage(response.data.data.id);

          // _this.props.saveGoalInfo(goalAligns.length, _this.state.isFiscal);
          _this.props.saveGoalInfo(params.goal_aligns.length);
          $(window).scrollTop(0);
        }
        // reload this week component if we are on this week calendar tab
        if (document.getElementById('mywork-top')) {
          $(document).trigger('init_this_week_top');
        }

        let goalDetailsType = '';
        const { start_date, target_date, type, owner_id, team_id, id } = this.props.addOkrData.goalDetails;
        let timePeriod = `${start_date} - ${target_date}`;

        if (type === '1') {
          goalDetailsType = 'Team';
        }

        if (type === '2') {
          goalDetailsType = 'Personal';
        }

        if (!this.state.selectedOption?.['_isCustom']) {
          timePeriod = this.state.selectedOption?.name ?? timePeriod;
        }

        window.wb_track('OBJECTIVE_CREATE', {
          UserId: window.uData.user_id,
          OrgId: window.uData.org_id,
          Timestamp: Date.now(),
          TeamId: team_id,
          Type: goalDetailsType,
          OwnerId: owner_id,
          TimePeriod: timePeriod,
        });
      });
    }
  }

  compare(arr1, arr2) {
    if (!arr1 || !arr2) return;
    // compare two array values are equals
    // arr1 = [7,8]
    // arr2 = [8,7]
    let is_same = (arr1.length == arr2.length && arr1.every(e => arr2.includes(e)));
    return is_same ? false : true;
  }
  setRightPaneValue() {
    this.props.setRightPaneState();
    $(window).scrollTop(0);
  }

  filterTeamFunction = _.debounce((event) => {
    let input, filter;
    event.preventDefault();
    input = document.getElementById('TeamSearch');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > -1) {
      this.GoalServices.searchTeamName({ term: filter }).then((response) => {
        if (response.data) {
          this.setState({
            filter: {
              ...this.state.filter,
              Team: response.data,
            },
            limit: response.data.length,
          });
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  /**
   * Merges new custom field data from response with existing state data,
   * preserving savedValue attributes by matching field id
   * @param {Object} currentCustomData - Current customData from state
   * @param {Object} newCustomData - New customData from response
   * @returns {Object} Merged customData with preserved savedValue attributes
   */
  mergeCustomData(currentCustomData, newCustomData) {
    return this.customFieldMerger.mergeCustomData(currentCustomData, newCustomData);
  }

  select(event, valTeam) {
    event.stopPropagation();
    let _this = this;
    _this.setState({
      team_name: valTeam.name,
      team_id: valTeam.id,
      ownerDataSpinner: true
    });
    _this.GoalServices.getTeamNBizDetails({ teamId: valTeam.id }).then(
      function (response) {
        if (response.data) {
          let findDefaultData = response.data.team_details.filter(v => v.role === 1);
          //let findDefaultData = response.data.team_details;
          let defaultOwner = findDefaultData.length
            ? findDefaultData[0]
            : response.data.team_details[0]; // When Manage is ROL, next user becomes default
            console.log('defaultOwner',defaultOwner);
            console.log('state',_this.state.owner_name);
            console.log('state 2',_this.state.owner_id);

          const {customData} = _this.state;
          const mergedCustomData = _this.mergeCustomData(
            customData,
            response.data.groupCustomFields
          );

          _this.setState({
            ownerList: [...response.data.team_details],
            owner_name: _this.state.owner_name
              ? _this.state.owner_id != defaultOwner.userId
                ? defaultOwner.fullName
                : _this.state.owner_name
              : defaultOwner.fullName,
            owner_id: _this.state.owner_id
              ? _this.state.owner_id != defaultOwner.userId
                ? defaultOwner.userId
                : _this.state.owner_id
              : defaultOwner.userId,
            krOwnerPic: _this.state.krOwnerPic
              ? _this.state.owner_id != defaultOwner.userId
                ? defaultOwner.picture
                : _this.state.krOwnerPic
              : defaultOwner.picture,
            ownerDataSpinner: false,
            teamEn: !_this.state.teamEn,
            filter: {
              Team: [],
            },
            customData: mergedCustomData
          });
          _this.bizPermissionData(response.data.permission_biz);
        }
      }
    );
  }

  handleOwnerSelect(event, val) {
    event.stopPropagation();
    this.setState({
      owner_name: val.fullName,
      owner_id: val.userId,
      ownerEn: false,
      krOwnerPic: val.picture
    });
  }

  filterOwnerFunction = _.debounce((event) => {
    let input, filter;
    input = document.getElementById('OwnerSearch');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      this.GoalServices.getOwnerBySearch({ term: filter }).then((response) => {
        if (response.data) {
          this.setState({
            ownerList:
              this.state.ownerSearchVal &&
                this.state.ownerSearchVal.length > 1
                ? response.data
                : []
          });
        }
      });
    } else {
      this.setState({
        filter: {
          ...this.state.filter,
          ownerList: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  filterObjectiveAlignFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    input = document.getElementById('ObjAlign');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      this.GoalServices.searchObjectiveAlignment({ term: filter, type: 'okr_page' }).then(
        (response) => {
          if (response.data.data && response.data.data.goals) {
            let Data = response.data.data.goals.filter(
              val => val.id != this.props.goalDetails.id
            );
            this.setState({
              filter: {
                ...this.state.filter,
                ObjectiveAlign:
                  this.state.objAlignSearchVal &&
                    this.state.objAlignSearchVal.length > 1
                    ? Data
                    : []
              }
            });
            if (this.state.objAlignSearchVal.length > 1 && Data.length > 0) {
              $('.aEGoalContent').parent().animate({ scrollTop: 255 });
            }
          }
        }
      );
    } else {
      this.setState({
        filter: {
          ...this.state.filter,
          ObjectiveAlign: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  filterDependenciesFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    input = document.getElementById('Dependency');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      this.GoalServices.searchObjectiveAlignment({ term: filter, type: 'okr_page' }).then(
        (response) => {
          if (response.data.data && response.data.data.goals) {
            let Data = response.data.data.goals.filter(
              val => val.id != this.props.goalDetails.id
            );
            this.setState({
              filter: {
                ...this.state.filter,
                dependencies:
                  this.state.dependencySearchVal &&
                    this.state.dependencySearchVal.length > 1
                    ? Data
                    : []
              }
            });

            if (this.state.dependencySearchVal.length > 1 && Data.length > 0) {
              $('.aEGoalContent').parent().animate({ scrollTop: 660 });
            }
          }
        }
      );
    } else {
      this.setState({
        filter: {
          ...this.state.filter,
          dependencies: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  filterCategoriesFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    input = document.getElementById('Category');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > -1) {
      this.GoalServices.searchObjectiveCategory({ term: filter }).then(
        (response) => {
          if (response.data) {
            this.setState({
              filter: {
                ...this.state.filter,
                Categories: response.data
              },
              limit: response.data.length
            });

            if (

              response.data.length > 0
            ) {
              $('.Category').parent().animate({ scrollTop: 390 });
            }
          }
        }
      );
     }
  }, SEARCH_DEBOUNCE_MS);

  filterWorkStreamFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    input = document.getElementById('WorkStream');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      this.GoalServices.searchWorkStream({ term: filter }).then((response) => {
        if (response.data) {
          const responseData = response.data.data || [];
          this.setState({
            filter: {
              ...this.state.filter,
              WorkStream:
                this.state.workstreamSearchVal &&
                  this.state.workstreamSearchVal.length > 1
                  ? responseData
                  : []
            }
          });

          if (
            this.state.workstreamSearchVal.length > 1 &&
            response.data.length > 0
          ) {
            $('.aEGoalContent').parent().animate({ scrollTop: 750 });
          }
        }
      });
    } else {
      this.setState({
        filter: {
          ...this.state.filter,
          WorkStream: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  filterPillarsFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    let _this = this;
    input = document.getElementById('Pillars');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      _this.GoalServices.searchPillars(filter).then(
        function (response) {
          if (response.data.data) {
            let Data = response.data.data.map((p) => ({
              id: p.pillarId,
              canView: true, // pillars from search are always visible (or they wont be returned)
              ...p,
            }));
            _this.setState({
              strategyBaseUrl: response.data?.strategyUrl,
              filter: {
                ..._this.state.filter,
                pillars:
                  _this.state.pillarsSearchVal &&
                    _this.state.pillarsSearchVal.length > 1
                    ? Data
                    : []
              }
            });

            if (_this.state.pillarsSearchVal.length > 1 && Data.length > 0) {
              $('.aEGoalContent').parent().animate({ scrollTop: 660 });
            }
          }
        }
      );
    } else {
      _this.setState({
        filter: {
          ..._this.state.filter,
          pillars: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);

  filterContinuityFunction = _.debounce((event) => {
    event.preventDefault();
    event.stopPropagation();
    let input, filter;
    let _this = this;
    input = document.getElementById('Continuity');
    filter = input.value.toLowerCase();
    filter[0] === ' ' ? filter[0].replace(' ', '') : filter;
    filter[filter.length - 1] === ' '
      ? filter[filter.length - 1].replace(' ', '')
      : filter;

    if (filter.length > 1) {
      let { start_date } = this.getOkrDuration();
      _this.GoalServices.searchObjectiveAlignment({ term: filter, type: 'okr_continuity', goal_start: moment(start_date).format('MMM DD, YYYY')}).then(
        function (response) {
          if (response.data.data && response.data.data.goals) {
            let Data = response.data.data.goals.filter(
              val => val.id != _this.props.goalDetails.id
            );
            _this.setState({
              filter: {
                ..._this.state.filter,
                copied_from_id:
                  _this.state.continuitySearchVal &&
                    _this.state.continuitySearchVal.length > 1
                    ? Data
                    : []
              }
            });

            if (_this.state.continuitySearchVal.length > 1 && Data.length > 0) {
              $('.aEGoalContent').parent().animate({ scrollTop: 660 });
            }
          }
        }
      );
    } else {
      _this.setState({
        filter: {
          ..._this.state.filter,
          copied_from_id: []
        }
      });
    }
  }, SEARCH_DEBOUNCE_MS);


  parseDate (date) {
    const isNumeric = typeof date === 'number' || !isNaN(date);
    return isNumeric ? moment.unix(Number(date)).utc().startOf('day') : moment.utc(date).startOf('day');
  };

  flattenGoalMetrics(goalDetails) {
    const out = [];
    const visitedMetricIds = new Set();
    const visit = (list, depth) => {
      if (depth > MAX_METRIC_TREE_WALK_DEPTH) {
        return;
      }
      (list || []).forEach((m) => {
        if (!m) return;
        if (m.id != null && m.id !== '') {
          if (visitedMetricIds.has(m.id)) {
            return;
          }
          visitedMetricIds.add(m.id);
        }
        out.push(m);
        visit(m.nested_krs, depth + 1);
        visit(m.child_krs, depth + 1);
      });
    };
    visit(goalDetails?.metrics || [], 0);
    return out;
  }

  metricHasAnyPopulatedDate(m) {
    if (!m) return false;
    const s = m.start_date;
    const e = m.end_date;
    const sSet = s !== null && s !== undefined && s !== '';
    const eSet = e !== null && e !== undefined && e !== '';
    return sSet || eSet;
  }

  formatKrPeriodSummary(metric) {
    const { t } = this.props;
    const idPart =
      metric.id != null && metric.id !== ''
        ? `${t('KR')} #${metric.id}`
        : t('KR (unknown id)');
    const fmt = (d) => {
      if (d === null || d === undefined || d === '') {
        return null;
      }
      const parsed = this.parseDate(d);
      return parsed.isValid() ? parsed.format('MMM D, YYYY') : String(d);
    };
    const start = fmt(metric.start_date);
    const end = fmt(metric.end_date);
    let range;
    if (start && end) {
      range = `${start} – ${end}`;
    } else if (start) {
      range = `${start} – ${t('(no end date)')}`;
    } else if (end) {
      range = `${t('(no start date)')} – ${end}`;
    } else {
      range = t('(dates unavailable)');
    }
    return `${idPart} · ${range}`;
  }

  handleRemoveAlign () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removeAlignBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ alignmentEn: true })
      }
  }

  handleAlignChange(event, selectedGoal, service) {

    event.stopPropagation();
    let existingState = this.state.ObjectiveAlingSelected;
    let result = [];
    if (existingState.find(val => val.id == selectedGoal.id)) {
      result = existingState.filter(eachVal => eachVal.id != selectedGoal.id);
    } else {
      result = [...existingState, selectedGoal];
    }
    let okrAlignsEvents = this.state.okrAlignsEvents;
    if (service == 'nlp') {
      okrAlignsEvents = [...okrAlignsEvents, selectedGoal.id];
    }
    this.setState({
      ObjectiveAlingSelected: result,
      okrAlignsEvents
    },() =>{
      this.handleRemoveAlign();
    });
  }

  handleRemoveDependency () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removeDependencyBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ dependenciedEn: true })
      }
  }
  handleRemovePillars () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removePillarsBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ pillarsArchiEn: true })
      }
  }
  handleRemoveContinuity () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removeContinuityBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ continuityEn: true })
      }
  }

  handleDependecyChange(event, selectedDependency) {
    event.stopPropagation();
    let existingState = this.state.dependenciesSelected;
    let result = [];
    if (existingState.find(val => val.id == selectedDependency.id)) {
      result = existingState.filter(
        eachVal => eachVal.id != selectedDependency.id
      );
    } else {
      result = [...existingState, selectedDependency];
    }
    this.setState({
      dependenciesSelected: result
    },() => {
      this.handleRemoveDependency();
    });
  }

  handlePillarsChange(event, pillar) {
    event.stopPropagation();
    let existingState = this.state.pillarsSelected;
    let result = [];
    if (existingState && existingState.find((p) => p.id == pillar.id)) {
      result = existingState.filter((p) => p.id != pillar.id);
    } else {
      result = [...existingState, pillar];
    }
    this.setState({
      pillarsSelected: result,
      pillarsDeleted: this.state.pillarsExisting.filter((pillar) => !result.find((p) => p.id === pillar.id))
    },() => {
      this.closePillars();
      this.handleRemovePillars();
    });
  }

  handleContinuityChange(event, selectedContinuity) {
    event.stopPropagation();
    let existingState = this.state.continuitySelected;
    let result = '';
    if (existingState && existingState.id == selectedContinuity.id) {
      result = '';
    } else {
      result = selectedContinuity;
    }
    this.setState({
      continuitySelected: result
    },() => {
      this.closeContinuity();
      this.handleRemoveContinuity();
    });
  }

  closePillars() {
    this.setState({ pillarsEn: !this.state.pillarsEn });
    if (!this.state.pillarsEn) {
      this.setState({
        filter: {
          ...this.state.filter,
          pillars: []
        }
      });
    }
  }

  closeContinuity(event) {
      this.setState({ continuityEn: !this.state.continuityEn });
      if (!this.state.continuityEn) {
        this.setState({
          filter: {
            ...this.state.filter,
            copied_from_id: []
          }
        });
      }
  }
  closeCategory(event) {
    event.stopPropagation();
    this.setState({ categoryEn: !this.state.categoryEn });
    let _this = this;
    if (!this.state.categoryEn) {
      this.setState({
        filter: {
          ...this.state.filter,
          Categories: []
        }
      });
    }
    var scrollTop = $('.aEGoalContent').scrollTop();
    var topOffset = $('.objCategory').offset().top;
    var relativeOffset = topOffset - scrollTop;
    var windowHeight = $('.aEGoalContent').height();
    if (relativeOffset > windowHeight / 2) {
      this.setState({
        setreverseCat: ''
      });
    } else {
      this.setState({
        setreverseCat: ''
      });
    }

  }

  handleRemoveCategory () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removeCategoryBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn && !this.state.categoryEn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ categoryEn: true })
      }
  }

  handleCategoryChange(event, mainSelectedGoal, selectedGoal) {
    this.setState({categoryRequired:false})
    event.stopPropagation();
    let existingState = this.state.categorySelected;
    let mainCategory = existingState.find(
      val => val.group_category_id == mainSelectedGoal.group_category_id
    );
    let result = [];
    if (mainCategory) {
      if (
        mainCategory.sub_categories.find(subVal => subVal.id == selectedGoal.id)
      ) {
        if (mainCategory.sub_categories.length > 1) {
          let filter = mainCategory.sub_categories.filter(
            eachSubVal => eachSubVal.id != selectedGoal.id
          );
          result = [
            ...existingState.filter(
              mainCat =>
                mainCat.group_category_id != mainSelectedGoal.group_category_id
            ),
            {
              ...mainCategory,
              sub_categories: filter
            }
          ];
        } else {
          result = [
            ...existingState.filter(
              mainCat =>
                mainCat.group_category_id != mainSelectedGoal.group_category_id
            )
          ];
        }
      } else {
        mainCategory.sub_categories.push(selectedGoal);
        result = [
          ...existingState.filter(
            mainCat =>
              mainCat.group_category_id != mainSelectedGoal.group_category_id
          ),
          mainCategory
        ];
      }
    } else {
      let filter = mainSelectedGoal.sub_categories.filter(
        eachSubVal => eachSubVal.id == selectedGoal.id
      );
      result = [
        ...existingState,
        {
          ...mainSelectedGoal,
          sub_categories: filter
        }
      ];
    }
    if(result[0] && result[0].length<1 && !result[1] ){
      result=[]
    }
    this.setState({
      categorySelected: result
    }, () => {
      this.handleRemoveCategory();
    });
  }

  handleRemoveWorkstream () {
    const focusableRemoveElements = document.querySelectorAll(
      '[id^="removeWorkstreamBtn"]'
      )
      const focusableRemoveArr = Array.prototype.slice.call(focusableRemoveElements);
      const firstRemoveBtn = focusableRemoveArr[0];
      if (firstRemoveBtn) {
        firstRemoveBtn?.focus();
      } else {
        const isTabbing = document.body.classList.contains('is-tabbing');
        if (isTabbing) this.setState({ achievingEn: true })
      }
  }

  handleWorkStreamChange(event, mainWS, subWS) {
    event.stopPropagation();
    let existingState = this.state.workStreamSelected;
    let mainWSTeam = existingState.find(val =>
      mainWS.team_id
        ? val.team_id == mainWS.team_id
        : val.team_name == mainWS.team_name
    );
    let result = [];
    if (mainWSTeam) {
      if (mainWSTeam.ws.find(subWork => subWork.id == subWS.id)) {
        if (mainWSTeam.ws.length > 1) {
          let filter = mainWSTeam.ws.filter(
            eachSubWork => eachSubWork.id != subWS.id
          );
          result = [
            ...existingState.filter(mainTeam =>
              mainWS.team_id
                ? mainTeam.team_id != mainWS.team_id
                : mainTeam.team_name != mainWS.team_name
            ),
            {
              ...mainWSTeam,
              ws: filter
            }
          ];
        } else {
          result = [
            ...existingState.filter(mainTeam =>
              mainWS.team_id
                ? mainTeam.team_id != mainWS.team_id
                : mainTeam.team_name != mainWS.team_name
            )
          ];
        }
      } else {
        mainWSTeam.ws.push(subWS);
        result = [
          ...existingState.filter(mainTeam => {
            if (mainWSTeam.team_id) {
              return mainTeam.team_id != mainWSTeam.team_id;
            } else {
              return mainTeam.team_name != mainWSTeam.team_name;
            }
          }),
          mainWSTeam
        ];
      }
    } else {
      let filter = mainWS.ws.filter(eachWS => eachWS.id == subWS.id);
      result = [
        ...existingState,
        {
          ...mainWS,
          ws: filter
        }
      ];
    }
    this.setState({
      workStreamSelected: result
    }, () => {
      this.handleRemoveWorkstream();
    });
  }

  closeWsPopUp(MainWS) {
    if (MainWS != undefined) {
      this.state.workStreamSelected.push(MainWS);
      this.setState({
        workStreamSelected: this.state.workStreamSelected,
        createWsPopUp: false
      });
    } else {
      this.setState({
        createWsPopUp: false
      });
    }
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  handleClickOutside(e) {
    let that = this;
    let enableObj = {};
    let filterObj = {
      filter: {}
    };
    if (
      !$('.aETeamSelect').is(e.target) &&
      $('.aETeamSelect').has(e.target).length === 0 &&
      (!$('.myTeamDropDown ').is(e.target) &&
        $('.myTeamDropDown').has(e.target).length === 0)
    ) {
      enableObj['teamEn'] = false;
      filterObj.filter['Team'] = [];
    }

    if (
      !$('.aEOwnerSelect').is(e.target) &&
      $('.aEOwnerSelect').has(e.target).length === 0 &&
      (!$('.myOwner ').is(e.target) && $('.myOwner').has(e.target).length === 0)
    ) {
      //enableObj['ownerList'] = [];
      //&& (!$('.aETeamSelect').is(e.target) && $('.aETeamSelect').has(e.target).length === 0)
      enableObj['ownerEn'] = false;
      filterObj.filter['owner'] = [];
    }

    if (
      !$('.objAlignment').is(e.target) &&
      $('.objAlignment').has(e.target).length === 0
    ) {
      enableObj['alignmentEn'] = false;
      filterObj.filter['ObjectiveAlign'] = [];
    }

    if (
      !$('.objCategory').is(e.target) &&
      $('.objCategory').has(e.target).length === 0
    ) {
      enableObj['categoryEn'] = false;
      filterObj.filter['Categories'] = [];
    }
    if (
      !$('.objDependencies').is(e.target) &&
      $('.objDependencies').has(e.target).length === 0
    ) {
      enableObj['dependenciedEn'] = false;
      filterObj.filter['dependencies'] = [];
    }

    if (
      !$('.objPillars').is(e.target) &&
      $('.objPillars').has(e.target).length === 0
    ) {
      enableObj['pillarsEn'] = false;
      filterObj.filter['pillars'] = [];
    }

    if (
      !$('.objContinuity').is(e.target) &&
      $('.objContinuity').has(e.target).length === 0
    ) {
      enableObj['continuityEn'] = false;
      filterObj.filter['continuity'] = [];
    }

    if (
      !$('.objectiveAchi').is(e.target) &&
      $('.objectiveAchi').has(e.target).length === 0
    ) {
      enableObj['achievingEn'] = false;
      filterObj.filter['WorkStream'] = [];
    }

    if (Object.keys(enableObj).length && Object.keys(filterObj).length) {
      this.setState({
        ...enableObj,
        filter: { ...this.state.filter, ...filterObj.filter }
      });
    }
    if (
      !$('.AlignmentLists-suggestion').is(e.target) &&
      $('.AlignmentLists-suggestion').has(e.target).length === 0
    ) {
      if(this.state.enAlignSuggestion == true && this.state.ObjectiveAlingSelected.length == 0 && this.state.okrAlignmentSuggestions.length != 0 ){
       let okrIds = this.state.okrAlignmentSuggestions.map((x)=>x.id)
        this.GoalServices.nlpEventTrack({ref:'NLP_OKR_ALIGNMENT_DECLINE',okrId:okrIds})
      }
      this.setState({ enAlignSuggestion: false })
    }
    setTimeout(function () {
      if (
        !$('.dateShowPopup').is(e.target) &&
        $('.dateShowPopup').has(e.target).length === 0
      ) {
        if (that.startDatePicker) that.startDatePicker.hideDayPicker();
        if (that.endDatePicker) that.endDatePicker.hideDayPicker();
      }
    }, 0);
  }
  onEnterPress(e) {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.triggerNlpOkrAlignmentService();
    }
    autoResizeTextArea(this.goalEditTextArea);
  }

  triggerNlpOkrAlignmentService()
  {
    const goalDetails = this.props.goalDetails;
    const goalName = this.state.editGoalName;
    if (goalDetails.type == 1 && this.captureOldName != goalName) {
      this.captureOldName = goalName;
      const { start_date, end_date } = this.getOkrDuration();
      this.GoalServices.nlpOkrAlignment({q: goalName, start_date, end_date, team_id: this.state.team_id})
      .then(response => {
        console.log(response.data)
        this.setState({okrAlignmentSuggestions: response.data.data || []});
        if (response.data.data.length > 0) {
          let okrIds = response.data.data.map((x)=>x.id)
          this.GoalServices.nlpEventTrack({ref:'NLP_OKR_ALIGNMENT_FOUND',okrId:okrIds})
        }
      });
    }
  }

  onBlurEvent(event) {
    this.triggerNlpOkrAlignmentService();
  }

  handleGoalNameChange(e) {
    autoResizeTextArea(this.goalEditTextArea);
    this.setState({
      editGoalName: e.target.value
    });
  }

  showStartDatePicker() {
    this.endDatePicker.hideDayPicker();
    this.startDatePicker.showDayPicker();
  }

  showEndDatePicker() {
    this.startDatePicker.hideDayPicker();
    this.endDatePicker.showDayPicker();
  }

  handleGoalNarrativeChange(data) {
    //autoResizeTextArea(this.goalNarrative);
    this.setState({
      narrative: data
    });
  }

  displayWorkDependent() {
    this.setState({
      wsArchiEn: !this.state.wsArchiEn,
      continuityArchiEn: !this.state.continuityArchiEn,
      divWsArchiEn: !this.state.divWsArchiEn,
      pillarsArchiEn: (wbConfig?.isPillarAlignmentEnabled) ? !this.state.pillarsArchiEn : false
    });
  }

  toggelePermission() {
    this.setState({
      permissonEn: true
    });
  }

  toggeleSubPermission() {
    this.setState({
      subPermissonEn: true,
      permissonEn: true
    });
  }

  renderError(field) {
    return (
      <ErrorTextComp key={field} errors={this.state.errors} field={field} />
    );
  }

  moveCaretAtEnd(e) {
    var temp_value = e.target.value;
    e.target.value = '';
    e.target.value = temp_value;
    this.setState({
      errors: ''
    });
  }

  permissionData(perData) {
    this.setState({
      permissionstate: perData
    });
  }

  teamPermissionData(teamPerData) {
    this.setState({
      teamPermissionSelected: teamPerData
    });
  }

  bizPermissionData = (bizPerData) => {
    if(bizPerData) {
      this.setState({
        bizPermissionSelected: bizPerData
      });
    }
  }

  enableDepdency(event) {
    this.setState({
      dependenciedEn: !this.state.dependenciedEn
    });

    event.stopPropagation();
    let scrollTops = $('.aEGoalContent').scrollTop();
    let topOffsets = $('.objDependencies').offset().top;
    let relativeOffsets = topOffsets - scrollTops;
    let windowHeights = $('.aEGoalContent').height();
    if (relativeOffsets > windowHeights / 2) {
      this.setState({
        setreverseDep: 'reverseBottom'
      });
    } else {
      this.setState({
        setreverseDep: ''
      });
    }
  }

  enablePillars(event) {
    this.setState({
      pillarsEn: !this.state.pillarsEn
    });

    event.stopPropagation();
    let scrollTops = $('.aEGoalContent').scrollTop();
    let topOffsets = $('.objPillars').offset().top;
    let relativeOffsets = topOffsets - scrollTops;
    let windowHeights = $('.aEGoalContent').height();
    if (relativeOffsets > windowHeights / 2) {
      this.setState({
        setreversePillars: 'reverseBottom'
      });
    } else {
      this.setState({
        setreversePillars: ''
      });
    }
  }

  enableContinuity(event) {
    this.setState({
      continuityEn: !this.state.continuityEn
    });

    event.stopPropagation();
    let scrollTops = $('.aEGoalContent').scrollTop();
    let topOffsets = $('.objContinuity').offset().top;
    let relativeOffsets = topOffsets - scrollTops;
    let windowHeights = $('.aEGoalContent').height();
    if (relativeOffsets > windowHeights / 2) {
      this.setState({
        setreverseContinuity: 'reverseBottom'
      });
    } else {
      this.setState({
        setreverseContinuity: ''
      });
    }
  }

  onChangeFilters(e, item) {
    this.setState({
      selectedOption: item,
      start_date: item.start,
      end_date: item.end
    });
  }

  periodNameDisplay(value) {
    const { t } = this.props
    let year = value.okrPeriod??'FY' + moment(value.target_date,'MMM DD, YYYY').format('YY') ; // for fix ww-18856
    if(year.includes('Q') || year.includes('FY')){
      let yearArray = year.split(' ');
      year = (year.includes('Q') ? yearArray[0] :'') + ` ${t('FY')} `+ `${ yearArray[1]}`.substring(2);
    }
    return year;
  }

  setEditor = (editor) => {
    this.setState({
      ck_editor: editor
    });
  }

  editObjhelpPopperHandler(e, key, link) {
    this.setState({ enAlignSuggestion: false})
    if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.setState(prevState => ({ [key]: !prevState[key] }));
    } else if (e.key === 'Escape' || (e.shiftKey && e.key === 'Tab')) {
      e.stopPropagation();
      this.setState({ [key]: false })
    } else if (!link && e.key === 'Tab') {
      e.stopPropagation();
      this.setState({ [key]: false })
    }
  }
  editObjhelpPopperTabHandler(e, key, id) {
    if (e.key === 'Tab') {
      e.stopPropagation();
      this.setState({ [key]: false })
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      this.setState({ [key]: false })
      setTimeout(() => {
        document.getElementById(id).focus();
      });
    }
  }

  okrAlignKeyDown(e,key,objVal, DocActiveId, ObjId) {
    if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleAlignChange(e, objVal, 'nlp')
    } else if (DocActiveId === ObjId || e.key === 'Escape' || (e.shiftKey && e.key === 'Tab')) {
      e.stopPropagation();
      this.setState({ [key]: false })
    }
  }



  render() {
    let goalDetails = this.props.goalDetails;
    let { errors, start_date, end_date, filterDateArray, ck_editor,
      okrAlignmentSuggestions } = this.state;
    let goalStartDateProps = { dayPickerProps: {} };
    let goalEndDateProps = { dayPickerProps: {} };
    let goalStartDate = start_date
      ? moment(start_date).format('MMM DD, YYYY')
      : '';
    let goalEndDate = end_date ? moment(end_date).format('MMM DD, YYYY') : '';
    let selectedStartDateString = undefined;
    if (start_date) {
      let startDateString = moment(start_date).toDate();
      selectedStartDateString = startDateString;
      goalEndDateProps.dayPickerProps = {
        month: startDateString,
        fromMonth: startDateString, // prevent previous month //WW-2692
        disabledDays: {
          before: startDateString
        }
      };
      goalStartDateProps.dayPickerProps.month = selectedStartDateString;
    }
    let selectedDateString = undefined;
    if (end_date) {
      selectedDateString = moment(end_date).toDate();
      goalEndDateProps.dayPickerProps.month = selectedDateString;
    }
    goalStartDateProps.dayPickerProps.selectedDays = selectedStartDateString;
    goalEndDateProps.dayPickerProps.selectedDays = selectedDateString;

    let isErrors = Object.keys(errors).length;

    const { enableModules = {} } = goalDetails;
    const executeModule = enableModules.executeModule || false;
    const { t } = this.props;
    const totalOkrSuggestions = okrAlignmentSuggestions?.length || 0;
    let _this = this;

    const flatMetricsForDuration = this.flattenGoalMetrics(goalDetails);
    const krsWithExplicitDates = flatMetricsForDuration.filter((m) =>
      this.metricHasAnyPopulatedDate(m)
    );
    const backendSaysCustomDates = !!goalDetails?.key_results_have_custom_dates_fy_objective;
    const hasExplicitDatesInTree = krsWithExplicitDates.length > 0;

    const durationChangeReasons = [];
    if (
      backendSaysCustomDates ||
      hasExplicitDatesInTree ||
      this.state.isNested
    ) {
      durationChangeReasons.push({
        key: 'kr-dates',
        text: t('At least one Key Result has dates not matching the Objective.'),
        detailLines: hasExplicitDatesInTree
          ? krsWithExplicitDates.map((m) => this.formatKrPeriodSummary(m))
          : null,
      });
    }
    if (goalDetails?.canChangeDuration === false) {
      durationChangeReasons.push({
        key: 'cascade',
        text: t('At least one Key Result is Cascaded.'),
        detailLines: null,
      });
    }

    const showDurationIntroAboveList = durationChangeReasons.length > 0;

    const durationConstraintsLabelAddon = showDurationIntroAboveList ? (
      <div className="editObj-help_tooltip-position" style={{ marginLeft: 4, marginTop: -2 }}>
        <StyledIconButton
          aria-describedby="objective_duration-help_tooltip"
          aria-label={t('Help')}
          onClick={(e) => this.editObjhelpPopperHandler(e, 'durationHelpPopUp', false)}
          onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'durationHelpPopUp', false)}
          id="objective_duration-help_tooltip_id"
        >
          <HelpOutline />
        </StyledIconButton>
        {this.state.durationHelpPopUp && (
          <div
            className="form-popup editObjTooltipContentPosition"
            id="objective_duration-help_tooltip"
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              color: 'rgba(0, 0, 0, 0.84)',
              fontStyle: 'normal',
            }}
            onMouseEnter={() => {
              this.setState({ durationHelpPopUp: true });
            }}
            onMouseLeave={() => {
              this.setState({ durationHelpPopUp: false });
            }}
            onKeyDown={(e) => {
              this.editObjhelpPopperTabHandler(e, 'durationHelpPopUp', 'objective_duration-help_tooltip_id');
            }}
          >
            <div>{t('Objective duration cannot be changed for the following reason(s):', { nsSeparator: false })}</div>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
              {durationChangeReasons.map((reason, idx) => (
                <li key={reason.key} style={{ marginTop: idx > 0 ? '8px' : 0 }}>
                  <div>{reason.text}</div>
                  {reason.detailLines && reason.detailLines.length > 0 ? (
                    <ul
                      style={{
                        margin: '6px 0 0 0',
                        paddingLeft: '18px',
                        listStyleType: 'circle',
                        fontSize: '13px',
                        lineHeight: 1.45,
                      }}
                    >
                      {reason.detailLines.map((line, lineIdx) => (
                        <li key={lineIdx} style={{ marginTop: lineIdx > 0 ? '4px' : 0 }}>
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
            <div style={{ marginTop: '8px' }}>{t('Adjust the Key Results first to change the Objective duration.')}</div>
          </div>
        )}
      </div>
    ) : null;

    return (
      <ThemeProvider>
        <StyledModal
          className="aE-Edit-Goal"
          dismissLabel={t('Cancel')}
          submitLabel={t('Save')}
          isOpen={true}
          removeClose
          onDismiss={this.setRightPaneValue}
          onSubmit={this.saveGoal}
          title={t('Edit Objective')}
          style={{ zIndex: '1000000', padding: 0, alignItems: 'flex-end', flexDirection: 'column' }}
          disableEnforceFocus
        >
          <div className="aEGoalContent" data-testid = "aEGoalContent">
            <div className="aERightPaneOkrSummary">
              <div className="inLineBlk divWidth50">
                <label className="objHeader " id="objHeader">
                  {t('Objective')} <span className="star">*</span>
                </label>
              </div>
              {
                wbConfig.default_org_locale == 'locale_1' &&
                  <div className="inLineBlk divWidth50 marginOut">
                    <a
                      className="objsmapleOkrs"
                      href="https://www.workboard.com/okrs-by-function/"
                      target="_blank"
                    >
                      {t('See sample OKRs')}
                    </a>
                  </div>
              }
            </div>

            <div>
              <ObjectiveContainer>
                <textarea
                  id="addGoalValueTxt"
                  className="addGoalValueTxt"
                  onChange={event => this.handleGoalNameChange(event)}
                  onKeyDown={event => this.onEnterPress(event)}
                  onFocus={this.moveCaretAtEnd}
                  onBlur={event => this.onBlurEvent(event)}
                  ref={node => (this.goalEditTextArea = node)}
                  defaultValue={this.state.editGoalName}
                  aria-required={true}
                  aria-labelledby="objHeader"
                ></textarea>
                <CoauthorButton variant="icon" onClick={this.handleCoauthorObjectiveClick} />
                <div ref={this.coauthorNode} />
              </ObjectiveContainer>
              {this.renderError('editGoalName')}
            </div>

            <div className="aEOptionDescSec" data-testid="aEOptionDescSec">
              {!this.state.addOptionalDesc && (
                <div className="inLineBlk divWidth100">
                  <Button
                    variant="ghost"
                    className="EObjective-glabel"
                    onClick={event => {
                      this.setState({
                        addOptionalDesc: !this.state.addOptionalDesc
                      });
                    }}
                  >
                    {t('Add a description(optional)')}
                  </Button>
                </div>
              )}

              {this.state.addOptionalDesc && (
                <div>
                  <span className="detailsSpan">{t('Description')}</span>
                  <CKEditor
                    editor={ CKEDITOR.ClassicEditor }
                    data={ this.state.narrative }
                    placeholder={t('Add additional details here')}
                    config={{
                      licenseKey: window.ckeditorLicenseKey,
                      toolbar: {
                        items: [ 'bold', 'italic','underline', 'strikethrough', 'link','bulletedList', 'numberedList', 'horizontalLine', 'undo' ]
                      },
                      extraPlugins: [
                        'Autoformat',
                        'BlockQuote',
                        'CKFinder',
                        'EasyImage',
                        'FontColor',
                        'FontFamily',
                        'FontSize',
                        'Heading',
                        'HorizontalLine',
                        'Indent',
                        'IndentBlock',
                        'List',
                        'MediaEmbed',
                        'PasteFromOffice',
                        'Table',
                        'TableToolbar',
                        'TextTransformation'
                      ]
                    }}
                    onChange={(e, editor) => {
                      const data = editor.getData();
                      this.handleGoalNarrativeChange(data);
                    }}
                    onReady={editor => {
                      this.setEditor(editor);
                    }}
                    // onBlur={event => this.handleGoalNarrativeChange(event)}
                  />
                  <EmojiContainer
                    ck_editor={ck_editor}
                  />
                </div>
              )}
            </div>

            <div className="aEdivWidth600">
              {goalDetails.type == 1 && (
                <div className="inLineBlk aETeamHead">{t('Team')}</div>
              )}
              <div className="inLineBlk aEOwnerHead">{t('Owner')}</div>
            </div>

            <div className="aEdivWidth600">
              {goalDetails.type == 1 && (
                <div className="inLineBlk teamSearch">
                  <div
                    className="aETeamSelect truncate"
                    value={this.state.team_name}
                    tabIndex={0}
                    id = "teamsDropdown"
                    onKeyUp={e => {
                      this.handleKey(e,'team')
                    }}
                    onClick={event => {
                      this.setState({ teamEn: !this.state.teamEn, limit : 5, offset : 5 });
                      this.changeSearchBox(event, 'team');
                    }}
                  >
                    <span className="team-name-span" data-testid="team-name-span">
                      {this.state.team_name}
                    </span>
                    <span className="team-name_arrow arrowDown " />
                  </div>

                  {this.state.teamEn && (
                    <div
                      id="myTeam"
                      className="myTeamDropDown dropdown-content"
                    >
                      <input
                        type="text"
                        placeholder={t('Search for teams')}
                        id="TeamSearch"
                        className="aETeamSearch"
                        onChange={event => this.changeSearchBox(event, 'team')}
                        autoFocus={true}
                      />
                      <div className="teamListsSec" tabIndex={0} data-testid="teamListsSec">
                        {this.state.filter &&
                          this.state.filter.Team &&
                          this.state.filter.Team.map( (valTeam, index) => (
                            <span
                              key={valTeam.id}
                              style={{
                                marginLeft:'10px',
                                width: '80%',
                                display: 'flex'
                              }}
                              className="handCur truncate"
                              tabIndex={0}
                              {...(this.state.filter.Team.length === index + 1 ? {ref : this.fetchDataProp} : {})}
                              onKeyUp={e => {
                                this.handleKeyWithData(e,'teamSelect',valTeam)
                              }}
                              onClick={e => {
                                this.select(e, valTeam);
                              }}
                            >
                              {valTeam.name}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                  {this.renderError('team_name')}
                </div>
              )}

              <div className="ownerSearch inLineBlk">
                <div
                  className="aEOwnerSelect truncate"
                  value={this.state.owner}
                  onClick={event => {
                    !this.state.ownerDataSpinner &&
                      this.setState({ ownerEn: !this.state.ownerEn });
                  }}
                  data-testid="aEOwnerSelect"
                   tabIndex = {0}
                >
                  <img
                    src={this.state.krOwnerPic}
                    className="fitProfileImage" alt=""
                  />
                  <div className="truncate owner-text">
                    {this.state.owner_name}
                  </div>
                  {this.state.ownerList && this.state.ownerList.length > 0 ? (
                    <div className="search-arrow" />
                  ) : goalDetails.type == 2 ? (
                    <div className="search-arrow" />
                  ) : (
                        ''
                      )}
                </div>
                {this.state.ownerDataSpinner && (
                  <div className="Ownerspinner">
                    <ImgIconComp id="loadinggif" />
                  </div>
                )}
                {this.state.ownerEn &&
                  !this.state.ownerDataSpinner &&
                  goalDetails.type == 1 &&
                  this.state.ownerList &&
                  this.state.ownerList.length > 0 ? (
                    <div id="myOwner" className="myOwner dropdown-content">
                      <div className="personal-owner" role="listbox">
                        {this.state.ownerList &&
                          this.state.ownerList.map(val => (
                            <div
                              className="ownerSec"
                              key={val.id}
                              data-testid="ownerSec"
                              role="option"
                              aria-selected={String(val.userId) === String(this.state.owner_id)}
                              tabIndex={0}
                              onClick={e => {
                                this.handleOwnerSelect(e, val);
                              }}
                              onKeyDown={e => {
                                if (e.keyCode === 13 || e.keyCode === 32) {
                                  e.preventDefault();
                                  this.handleOwnerSelect(e, val);
                                }
                              }}
                            >
                              <UserItemRenderer option={val} />
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : (
                    goalDetails.type == 2 &&
                    this.state.ownerEn &&
                    !this.state.ownerDataSpinner && (
                      <div id="myOwner" className="myOwner ">
                        <input
                          type="text"
                          placeholder={t('Search for people')}
                          id="OwnerSearch"
                          className="aEOwnerSearch"
                          onChange={event => this.changeSearchBox(event, 'owner')}
                          autoFocus={true}
                        />
                        <div className="personal-owner" role="listbox">
                          {this.state.ownerList &&
                            this.state.ownerList.map(val => (
                              <div
                                className="ownerSec"
                                key={val.id}
                                data-testid="ownerSec"
                                role="option"
                                aria-selected={String(val.userId) === String(this.state.owner_id)}
                                tabIndex={0}
                                onClick={e => {
                                  this.handleOwnerSelect(e, val);
                                }}
                                onKeyDown={e => {
                                  if (e.keyCode === 13 || e.keyCode === 32) {
                                    e.preventDefault();
                                    this.handleOwnerSelect(e, val);
                                  }
                                }}
                              >
                                <UserItemRenderer option={val} />
                              </div>
                            ))}
                        </div>
                      </div>
                    )
                  )}
              </div>
              {this.renderError('owner_validation')}
              <div className="clear" />
            </div>
            {this.state.filterDateArray.length > 0 && (
              <>
                <DurationSelect
                  id="dropdowncomp-btn-edit-goal-duration"
                  style={{ marginTop: '16px' }}
                  timePeriodOptions={this.state.filterDateArray}
                  config={{
                    enablePeriodFetch: true,
                  }}
                  disabled={this.state.isNested || this.props.goalDetails?.canChangeDuration === false || this.props.goalDetails?.key_results_have_custom_dates_fy_objective === true}
                  initialValue={this.state.selectedOption}
                  onChange={(data) => {
                    this.onChangeFilters(undefined, data)
                  }}
                  labelAddon={durationConstraintsLabelAddon}
                />
              </>
            )}
            {this.renderError('kr_date_range')}

            <div className="aEdivWidth600 marginTop30 editObjhelpIconPosition">
              <div className="Objective-alignment" data-testid="Objective-alignment">{t('Objective alignment')}</div>
              <div className="editObj-help_tooltip-position">
              <StyledIconButton
                aria-describedby='objective_alignment-help_tooltip'
                aria-label='Help'
                onMouseEnter={event => {
                  this.setState({
                    alignsHelpPopUp: !this.state.alignsHelpPopUp
                  });
                }}
                onMouseLeave={event => {
                  this.setState({
                    alignsHelpPopUp: !this.state.alignsHelpPopUp
                  });
                }}
                onClick={(e) => this.editObjhelpPopperHandler(e, 'alignsHelpPopUp', true)}
                onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'alignsHelpPopUp', true)}
                id="objective_alignment-help_tooltip_id"
              >
                <HelpOutline />
              </StyledIconButton>
                {this.state.alignsHelpPopUp && (
                  <div className="form-popup editObjTooltipContentPosition" id="objective_alignment-help_tooltip"
                    onMouseEnter={event => { this.setState({ alignsHelpPopUp: true }) }}
                    onMouseLeave={event => { this.setState({ alignsHelpPopUp: false }) }}
                    onKeyDown={(e) => {
                      this.editObjhelpPopperTabHandler(e, 'alignsHelpPopUp', 'objective_alignment-help_tooltip_id')
                    }}
                  >
                    {`${t("Align this objective up to your manager's or other applicable OKR.")} ${t("The linkage will be visible from your OKR and the one(s) to which you've aligned.")}`}
                    <a
                      className="learnmore"
                      target="_blank"
                      href="https://workboard.zendesk.com/hc/en-us/articles/216294188-Goals-and-metrics"
                    >
                      {t('Learn more')}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <div
                className="inLineBlk objAlignment"
                style={{ paddingRight: 0, marginRight: 18 }}
              >
                <div
                  className="objAlignmentSearchdiv"
                  data-testid="align-search-box"
                  onClick={event => {
                    this.setState({ alignmentEn: !this.state.alignmentEn });
                  }}
                >
                  {t('Align this objective up to another one (or more)')}
                </div>
                {this.state.alignmentEn && (
                  <div id="myAlign" data-testid="myAlign" className="dropdown-alignment " style={{width:414}}>
                    <input
                      type="text"
                      placeholder={t('Align this objective up to another one (or more)')}
                      className="objAlignmentSearch"
                      id="ObjAlign"
                      data-testid="align-search-div"
                      onChange={event => this.changeSearchBox(event, 'objAlign')}
                      autoFocus={true}
                      style={{width:364}}
                    />
                    <div className="AlignmentLists">
                      {this.state.filter &&
                        this.state.filter.ObjectiveAlign &&
                        this.state.filter.ObjectiveAlign.map(objVal => (

                          <div className="myAlign" data-testid="myAlign" key={`Obj${objVal.id}`} style={{width:' 100% ' ,display: 'flex'}}>
                            <input
                              id={`Obj${objVal.id}`}
                              className="defaultUnCheck alignCheck inLineBlk"
                              onClick={e => this.handleAlignChange(e, objVal)}
                              type="checkbox"
                              defaultChecked={
                                this.state.ObjectiveAlingSelected.find(
                                  val => val.id == objVal.id
                                )
                                  ? true
                                  : false
                              }
                            />
                            <label htmlFor={`Obj${objVal.id}`} />
                            <div className="inLineBlk alignOdjList truncate" style={{width:'90%' , marginLeft:'-12px'}}>
                              <div className="alignOdjtex">{`${objVal.goal}`}</div>
                              <div className="alignObjInfo"  style={{ maxWidth:'360px', minWidth:'340px'}} >
                                {objVal.team_name && objVal.team_name != null && (
                                  <span>
                                    <span>{objVal.team_name} </span>
                                    <span className="dotClass"></span>
                                  </span>
                                )}
                                <span>
                                  {objVal.user_firstname} {objVal.user_lastname}
                                </span>
                                <span>
                                  {' '}
                                  <span className="dotClass"></span>
                                  <span>{_this.periodNameDisplay(objVal)}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              { totalOkrSuggestions > 0 &&
                <div
                  data-testid="objAlignment"
                  className="inLineBlk objAlignment"
                  style={{ position: 'relative', paddingRight: 0 }}
                >
                <div  style={{display:'flex' , justifyContent: 'space-between',}}>
                  <div
                  >
                     <Button
                    className="createWsBtn"
                    kind="normal"

                     style={{
                      display:'flex',
                      color: '#0091EA',
                      fontSize: '14px',
                      fontWeight: 400,
                      textAlign: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 15px 8px 15px',
                      marginLeft: '2px',
                      width: 'fit-content',
                      cursor: 'pointer',
                      height: '38px',
                      whiteSpace: 'nowrap',
                      background:
                        'linear-gradient(0deg, rgba(255, 255, 255, 0.84), rgba(255, 255, 255, 0.84)), #0091EA',
                    }}
                    onClick={this.enableAlignSuggestion}

                  >
              <ImgIconComp id="kr_cluster_1x" width='24' height='24' margin='8' className='icontipLightbulb'/>
              <div
                  style={{
                  paddingLeft: '10px',
                  }}>  {totalOkrSuggestions} {t('suggested objectives')}</div>
                  </Button>
                  </div>

                  </div>
                  {this.state.enAlignSuggestion && (
                  <div >
                    <div className={'upArrow' } style={{textAlign: 'center', left:'100px' }}></div>
                    <div
                      id="myAlign2"
                      data-testid="myAlign2"
                      className="dropdown-alignment"
                      aria-describedby='AlignmentLists-suggestion_map'
                      style={{
                        position: 'absolute',
                        top: '92px',
                        right: 0,
                        backgroundColor: '#ffffff',
                        padding: '8px',
                        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25), inset 0px 0px 1px #979797',
                        zIndex: 100,
                        maxWidth: '440px',

                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: '14px',
                          padding: '8px',
                        }}
                      >
                        {t(`We've found a few objectives you could align to -
                        take a look!`)}
                      </span>
                    <div>
                      <div className="AlignmentLists AlignmentLists-suggestion" id="AlignmentLists-suggestion">
                        {okrAlignmentSuggestions.map((objVal) => (
                          <div
                          id="AlignmentLists-suggestion_map"
                            className="myAlign"
                            data-testid="myAlign3"
                            key={`Obj${objVal.id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              margin:'2px',
                            }}
                          >
                          <div  style={{ marginLeft: 0 ,width:'fit-content',
                          height: 'fit-content' }}
                          onKeyDown ={(e) => this.okrAlignKeyDown(e,'enAlignSuggestion',objVal,document.activeElement.id,`Obj${ okrAlignmentSuggestions[okrAlignmentSuggestions.length-1].id}`)}
                           >
                          <StyledCheckbox
                           id={`Obj${objVal.id}`}
                          data-testid="ui-tag"
                          className="browse-checkbox"
                          style={{width:'fit-content',
                          height: 'fit-content' }}
                          onChange={(e) => this.handleAlignChange(e, objVal, 'nlp')}
                          isChecked={
                            this.state.ObjectiveAlingSelected.find(
                              (val) => val.id === objVal.id
                            )
                              ? true
                              : false
                          }
                          animation
                          />
                      </div>
                       <label
                            htmlFor={`Obj${objVal.id}`}
                            />
                            <div className="inLineBlk alignOdjList truncate" id ="t_name" style={{ marginLeft: 0 }} >
                              <div className="alignOdjtex">{`${objVal.goal}`}</div>
                              <div className="alignObjInfo">
                                {objVal.team_name && objVal.team_name != null && (
                                  <span>
                                    <span>{objVal.team_name} </span>
                                    <span className="dotClass"></span>
                                  </span>
                                )}
                                <span>
                                  {objVal.user_firstname} {objVal.user_lastname}
                                </span>
                                <span>
                                  {' '}
                                  <span className="dotClass"></span>
                                  <span>{_this.periodNameDisplay(objVal)}</span>
                                </span>
                              </div>
                            </div>
                          </div>

                        ))}
                      </div>
                      </div>
                    </div>
                  </div>
                  )}
                 </div>
               }

              </div>

            {this.state.ObjectiveAlingSelected.length > 0 && (
              <div className="selectedObj">{t('Aligns up to')}:</div>
            )}
            {this.state.ObjectiveAlingSelected &&
              this.state.ObjectiveAlingSelected.map(val => (
                <div className="selectedObj" data-testid="selected-aligned" key={val.id}>
                  <div className="selectedMyAlign selObjHigh">
                    <div className="inLineBlk alignOdjLists">
                      <div className="alignOdjtex" id={`selected-aligns-${val.id}`}>{val.goal}</div>
                      <div className="alignObjInfo">
                        {val.team_name && val.team_name != null && (
                          <span>
                            <span>{val.team_name} </span>
                            <span className="dotClass"></span>
                          </span>
                        )}
                        {val.user_firstname && val.user_firstname != null && (
                          <span>
                            {val.user_firstname} {val.user_lastname}
                          </span>
                        )}
                        <span>
                          {val.user_firstname && val.user_firstname != null && (
                            <span className="dotClass"></span>
                          )}
                          <span>{_this.periodNameDisplay(val)}</span>
                        </span>
                      </div>
                    </div>
                    <RemoveIconButton
                      aria-label={t('Remove')}
                      aria-describedby={`selected-aligns-${val.id}`}
                      id={`removeAlignBtn${val.id}`}
                      onClick={e => this.handleAlignChange(e, val)}
                    />
                  </div>
                </div>
              ))}

            <div className="aEPermissionDiv editObjhelpIconPosition">
              <div className="aEPermissionText">
                {t('View permissions in addition to')}{' '}
                {this.props.goalDetails.type == 1 ? t(' team ') : t(' owner ')}
              </div>
              <div className="editObj-help_tooltip-position">
              <StyledIconButton
                aria-describedby='view-permissions-help_tooltip'
                aria-label='Help'
                onMouseEnter={event => {
                  this.setState({
                    permissionHelpPopUp: !this.state.permissionHelpPopUp
                  });
                }}
                onMouseLeave={event => {
                  this.setState({
                    permissionHelpPopUp: !this.state.permissionHelpPopUp
                  });
                }}
                onClick={(e) => this.editObjhelpPopperHandler(e, 'permissionHelpPopUp', false)}
                onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'permissionHelpPopUp', false)}
                id="view-permissions-help-btn"
              >
                <HelpOutline />
              </StyledIconButton>
              {this.state.permissionHelpPopUp && (
                <div className="form-popup editObjTooltipContentPosition" id="view-permissions-help_tooltip"
                  onMouseEnter={event => { this.setState({ permissionHelpPopUp: true }) }}
                  onMouseLeave={event => { this.setState({ permissionHelpPopUp: false }) }}
                >
                  {`${t('Decide to whom this OKR is transparent.')} ${t('You can make it visible to just this team, the whole organization or choose specific teams.')}`}
                </div>
              )}
            </div>
            </div>

            <PermissionComp
              item={{...this.props.goalDetails, permission_biz: this.state.bizPermissionSelected}}
              goalDetails={this.props.goalDetails}
              teamPermissionData={this.teamPermissionData}
              bizPermissionData={this.bizPermissionData}
              permissionData={this.permissionData}
              Exclude={this.state.Exclude}
              handleExcludeChange={this.handleExcludeChange}
              hide={this.state.hide}
              okrDefaultPermittedTeam={this.props.addOkrData.okr_teamOnly_Permission}
              okr_disabled_org_perm={this.props.addOkrData.okr_disabled_org_perm}
            />
            {this.props.addOkrData.category_exist &&
              this.props.addOkrData.category_exist != 0 ? (
                <div className="Objective-category editObjhelpIconPosition">
                  <div style={{width:'128px'}}className="ObjectiveCategoryText" data-testid="ObjectiveCategoryText">
                    {t('Objective category')}
                    {((this.state.categoryTrueForPersonal && this.props.addOkrData.goalDetails.type==2)
                       ||(this.state.categoryTrueForTeamObjective && this.props.addOkrData.goalDetails.type==1)) &&
                      <span className="star">*</span>
                    }
                </div>
                  <div className="editObj-help_tooltip-position">
                    <StyledIconButton
                      aria-describedby='Objective-category-help_tooltip'
                      aria-label={t('Help')}
                      onMouseEnter={event => {
                        this.setState({
                          categoryHelpPopUp: !this.state.categoryHelpPopUp
                        });
                      }}
                      onMouseLeave={event => {
                        this.setState({
                          categoryHelpPopUp: !this.state.categoryHelpPopUp
                        });
                      }}
                      onFocus = {(e)=>{
                        this.setState({ categoryEn : false})
                      }}
                      onClick={(e) => this.editObjhelpPopperHandler(e, 'categoryHelpPopUp', false)}
                      onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'categoryHelpPopUp', false)}
                    >
                      <HelpOutline />
                    </StyledIconButton>
                    {this.state.categoryHelpPopUp && (
                      <div className="form-popup editObjTooltipContentPosition" id="Objective-category-help_tooltip"
                        onMouseEnter={event => { this.setState({ categoryHelpPopUp: true }) }}
                        onMouseLeave={event => { this.setState({ categoryHelpPopUp: false }) }}>
                        {`${t('Your organization may have categories of objectives that are convenient for searching and reviewing progress.')} ${t('Assign the category here.')}`}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                ''
              )}

            {this.props.addOkrData.category_exist &&
              this.props.addOkrData.category_exist != 0 ? (
                <div ref={this.scollToRef}  className="objCategory"
                  aria-invalid="false"
                  id="objCategory"
                  tabIndex={0}
                  onKeyUp={e => {
                    this.handleKey(e,'category')
                  }}
                  style={{border:this.state.categoryRequired?'1px solid #d93630':''}}
                  >
                  <div
                    className="objCategorySearchdiv"
                    data-testid="objCategorySearchdiv"
                    onClick={(event) => {
                      this.setState({ teamEn: !this.state.teamEn, limit : 5, offset : 5 });
                      this.closeCategory(event);
                      this.changeSearchBox(event, 'category');
                    }}
                  >
                    {t('Select a category')}
                </div>
                  {this.state.categoryEn && (
                    <div
                      id="myAlign"
                      className={'dropdown-alignment ' + this.state.setreverseCat}
                    >
                      <input
                        type="text"
                        id="Category"
                        data-testid="category-search-bar"
                        placeholder={t('Select a category')}
                        className="objCategorySearch"
                        onChange={event =>
                          this.changeSearchBox(event, 'category')
                        }
                        autoFocus={true}
                        required={true}
                      />


                      <div className="CateScroll">
                        {this.state.filter.Categories &&
                          this.state.filter.Categories.map((eachCat,index) => (
                            <div className="catLists" style={{overflow: 'hidden' , maxHeight: 'fit-content',
                            alignItems: 'center',
                            margin:'4px',}} tabIndex={0} key={`Cat${eachCat.id}`} {...(this.state.filter.Categories.length === index + 1 ? {ref : this.fetchObjCatProp} : {})}>
                              <div className="categoryTitle">
                                {eachCat.group_category_name}
                              </div>
                              {eachCat.sub_categories &&
                                eachCat.sub_categories.map(eachSub => (
                                  <div
                                    className="myAlign" data-testid="myAlign"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      margin:'3px',
                                    }}
                                    key={`SubCat${eachSub.id}`}
                                  >
                                    <StyledCheckbox
                                      className="browse-checkbox"
                                      tabIndex={0}
                                      onChange={event =>
                                        {
                                          this.handleCategoryChange(
                                            event,
                                            eachCat,
                                            eachSub
                                          )
                                        }
                                      }
                                      type="checkbox"
                                      isChecked={this.state.categorySelected.find(
                                        function (val) {
                                          if (
                                            val.group_category_id ==
                                            eachCat.group_category_id
                                          ) {
                                            return val.sub_categories.find(
                                              value =>
                                                value.id == eachSub.id
                                                  ? true
                                                  : false
                                            );
                                          }
                                        }
                                      )}
                                      id={'Cat_' + eachSub.id}
                                    />
                                    <label htmlFor={'Cat_' + eachSub.id} />
                                    <div className="inLineBlk alignOdjList" style={{
                                      padding:'1px'

                                    }}>
                                      <div className="alignOdjtex truncate">
                                        {eachSub.name}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  )
                  }
                   {this.state.categoryRequired ? <div style = {{ marginLeft: '-30px', paddingTop: '5px'}}>
                     {
                      this.renderError('categorySelected')
                      }
                    </div>: ''}
                </div>
              ) : (
                ''
              )}

            {this.state.categorySelected.length > 0 &&
              this.props.addOkrData.category_exist &&
              this.props.addOkrData.category_exist != 0 ? (
                <div className="selectedObj">{t('Selected category')}(s):</div>
              ) : (
                ''
              )}
            {this.state.categorySelected.length > 0 &&
              this.props.addOkrData.category_exist &&
              this.props.addOkrData.category_exist != 0
              ? this.state.categorySelected.map(
                eachMain =>
                  eachMain.sub_categories &&
                  eachMain.sub_categories.map(eachSub => (
                    <div className="selectedObj" data-testid="selected-categories" key={eachSub.id}>
                      <div className="selectedMyAlign selCatHigh">
                        <div className="inLineBlk alignOdjLists">
                          <div className="alignOdjtex" id={`selected-category-${eachSub.id}`}>{eachSub.name}</div>
                        </div>
                        <RemoveIconButton
                          data-testid="crossmark"
                          name="Clear"
                          aria-label={t('Remove')}
                          onFocus = {(e)=>{
                            this.setState({ categoryEn : false})
                          }}
                          aria-describedby={`selected-category-${eachSub.id}`}
                          id={`removeCategoryBtn${eachSub.id}`}
                          onClick={e =>
                            this.handleCategoryChange(e, eachMain, eachSub)
                          }
                        />
                      </div>
                    </div>
                  ))
              )
              : ''}
              {/* ............................... */}
              {
                (
                  this.props.goalDetails.groupCustomFields ||
                  this.props.addOkrData.groupOKRCustomFields ||
                  (this.state.customData && Object.keys(this.state.customData).length > 0)
                ) &&
                (
                <Customokr
                  customData={this.state.customData}
                  isRequiredCustomOkrFiled={this.isRequiredCustomOkrFiled}
                  onCustomDataChange={(newData) => {
                    this.setState({customData: newData, customFieldsData: buildCustomFieldsDataArray(newData, '5')})
                  }}
                  renderError={this.renderError}
                  customFiledRequired={this.state.customFiledRequired}
                  isCustomSave={this.state.isCustomSave}
                />
              )
              }

            {this.state.divWsArchiEn && (
              <div className="aEOptionDescSec" data-testid="aEOptionDescSec">
                <div className="inLineBlk divWidth100">
                  <Button
                    onFocus = {(e)=>{
                      this.setState({ categoryEn : false})
                    }}
                    className="EObjective-glabel"
                    data-testid="aEOptionDesc"
                    onClick={this.displayWorkDependent}
                  >
                    {t('More')}
                  </Button>
                </div>
              </div>
            )}
            <AddFinancialTargetsButton objectType="okr" objectId={this.props.goalDetails?.id} ownerUserId={this.props.goalDetails.owner_id} />
            {this.state.pillarsArchiEn && (
              <>
                <div className="ObjectivePillarsText marginTop30" style={{ marginBottom: '8px' }}>{t('Align Objective to Strategy pillar')}</div>
                <div className="objPillars">
                  <div
                    className="objPillarsSearchdiv"
                    data-testid="searchOkrTeamPerson"
                    onClick={event => this.enablePillars(event)}
                  >
                    {t('Search for pillars')}
                  </div>
                  {this.state.pillarsEn && (
                    <div
                      id="myAlign"
                      className={
                        'dropdown-alignment ' + this.state.setreversePillars
                      }
                    >
                      <input
                        type="text"
                        placeholder={t('Search for pillars')}
                        className="objPillarsSearch"
                        id="Pillars"
                        data-testid="testPillarsSearch"
                        onChange={event =>
                          this.changeSearchBox(event, 'pillars')
                        }
                        autoFocus={true}
                        autoComplete="off"
                      />
                      <div className="pillarsLists">
                        {
                          this.state.filter?.pillars?.map(pillar => (
                            <div className="myAlign" data-testid="myAlign" key={`Pillars${pillar.id}`}>
                              <div
                                id={'Pillars_' + pillar.id}
                                className="alignCheck inLineBlk"
                                onClick={e =>
                                  this.handlePillarsChange(e, pillar)
                                }
                              >
                              <label htmlFor={'Pillars_' + pillar.id} />
                              <div className="inLineBlk alignOdjList">
                                <div className="alignOdjtex">
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgb(68, 75, 87)',
                                    borderRadius: '50%',
                                    height: '18px',
                                    width: '18px',
                                    marginRight: '8px'
                                  }}>
                                    <MUIIcon
                                      component={pillarIconMap[pillar.iconSvg]}
                                      style={{ fontSize: 11 }}
                                    />
                                  </span>
                                  {pillar.name}
                                </div>
                                <div className="alignObjInfo" style={{ marginLeft: '26px' }}>
                                  <span>
                                    <span>{`FY${moment(pillar?.strategy?.startDate).format('YY')}`}</span>
                                    <span className="dotClass"></span>
                                    <span>{`FY${moment(pillar?.strategy?.endDate).format('YY')}`}</span>
                                  </span>
                                  <span className="dotClass"></span>
                                  <span>
                                    {pillar?.strategy?.name}{' '}
                                  </span>
                                </div>
                              </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {this.state.pillarsArchiEn && this.state.pillarsSelected.length > 0 && (
                  <div className="selectedObj">{t('Aligns to')}:</div>
            )}
            {this.state.pillarsArchiEn && this.state.pillarsSelected.map((pillar) => (
              <div className="selectedObj" data-testid="selected-pillars" key={pillar.id}>
                <div className="selectedMyAlign selObjHigh">
                  <div className="inLineBlk alignOdjLists">
                    <div className="alignOdjtex" id={`selected-pillars-${pillar.id}`}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgb(68, 75, 87)',
                        borderRadius: '50%',
                        height: '18px',
                        width: '18px',
                        marginRight: '8px'
                      }}>
                        <MUIIcon
                          component={pillarIconMap[pillar.iconSvg]}
                          style={{ fontSize: 11 }}
                        />
                      </span>
                      {pillar.canView ? (
                        <a
                          href={`${this.state.strategyBaseUrl}strategy/${pillar?.strategy?.id}/pillars/${pillar.id}`}
                          target="_blank"
                          style={{
                            display: 'inline-block',
                            maxWidth: 'calc(100% - 30px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: 'currentColor',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {pillar.name}
                        </a>
                      ) : (
                        <span style={{color: 'rgba(10, 21, 55, 0.6)'}}>You don't have permission to view this Pillar</span>
                      )}
                    </div>
                    {pillar.canView && (<div className="alignObjInfo" style={{ marginLeft: '26px' }}>
                      <span>
                        <span>{`FY${moment(pillar?.strategy?.startDate).format('YY')}`}</span>
                        <span className="dotClass"></span>
                        <span>{`FY${moment(pillar?.strategy?.endDate).format('YY')}`}</span>
                      </span>
                      <span className="dotClass"></span>
                      <span>
                        {pillar?.strategy?.name}{' '}
                      </span>
                    </div>)}
                  </div>
                  <RemoveIconButton
                    name="Clear"
                    aria-label='Remove'
                    aria-describedby={`selected-pillars-${pillar.id}`}
                    id={`removePillarsBtn${pillar.id}`}
                    onClick={e => this.handlePillarsChange(e, pillar)}
                  />
                </div>
              </div>
            ))}
            { this.state.continuityArchiEn && (
              <div>
                <div className="aEObjectiveDep editObjhelpIconPosition">
                  <div className="ObjectiveDepText">{t('Objective is a carry over of')}</div>
                  <div className="editObj-help_tooltip-position">
                  <StyledIconButton
                    aria-describedby='Objective-continuity-help_tooltip'
                    aria-label={t('Help')}
                    onMouseEnter={event => {
                      this.setState({
                        continuityHelpPopUp: !this.state.continuityHelpPopUp
                      });
                    }}
                    onMouseLeave={event => {
                      this.setState({
                        continuityHelpPopUp: !this.state.continuityHelpPopUp
                      });
                    }}
                    onClick={(e) => this.editObjhelpPopperHandler(e, 'continuityHelpPopUp', true)}
                    onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'continuityHelpPopUp', true)}
                    id="Objective-continuity-help_tooltip_id"
                  >
                    <HelpOutline />
                  </StyledIconButton>
                  {this.state.continuityHelpPopUp && (
                    <div className="form-popup editObjTooltipContentPosition"
                      id="Objective-continuity-help_tooltip"
                      onMouseEnter={event => { this.setState({ continuityHelpPopUp: true }) }}
                      onMouseLeave={event => { this.setState({ continuityHelpPopUp: false }) }}
                      onKeyDown={(e) => {
                        this.editObjhelpPopperTabHandler(e, 'continuityHelpPopUp', 'Objective-continuity-help_tooltip_id')
                      }}
                    >
                      {t('Specify if an objective is a continuation of a previous one. Objectives marked as such will appear connected in the timeline view, highlighting the continued focus on the objective')}
                    </div>
                    )}
                  </div>
                </div>
                <div className="objContinuity">
                  <div
                    className="objContinuitySearchdiv"
                    data-testid="searchOkrTeamPerson"
                    onClick={event => this.enableContinuity(event)}
                  >
                    {t('Search for an OKR by person, objective name, or team')}
                  </div>
                  {this.state.continuityEn && (
                    <div
                      id="myAlign"
                      className={
                        'dropdown-alignment ' + this.state.setreverseContinuity
                      }
                    >
                      <input
                        type="text"
                        placeholder={t('Search for an OKR by person, objective name, or team')}
                        className="objContinuitySearch"
                        id="Continuity"
                        data-testid="testContinuitySearch"
                        onChange={event =>
                          this.changeSearchBox(event, 'continuity')
                        }
                        autoFocus={true}
                        autoComplete="off"
                      />
                      <div className="continuityLists">
                        {this.state.filter &&
                          this.state.filter.copied_from_id &&
                          this.state.filter.copied_from_id.map(eachVal => (
                            <div className="myAlign" data-testid="myAlign" key={`Continuity${eachVal.id}`}>
                              <div
                                id={'Continuity_' + eachVal.id}
                                className="alignCheck inLineBlk"
                                onClick={e =>
                                  this.handleContinuityChange(e, eachVal)
                                }
                              >
                              <label htmlFor={'Continuity_' + eachVal.id} />
                              <div className="inLineBlk alignOdjList">
                                <div className="alignOdjtex">
                                  {eachVal.goal}
                                </div>
                                <div className="alignObjInfo">
                                  {eachVal.team_name &&
                                    eachVal.team_name != null && (
                                      <span>
                                        <span>{eachVal.team_name} </span>
                                        <span className="dotClass"></span>
                                      </span>
                                    )}
                                  <span>
                                    {eachVal.user_firstname}{' '}
                                    {eachVal.user_lastname}{' '}
                                  </span>
                                  <span>
                                    <span className="dotClass"></span>
                                    <span>{this.periodNameDisplay(eachVal)}</span>
                                  </span>
                                </div>
                              </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                {this.state.continuitySelected && (
                  <div className="selectedObj">{t('Carry over of')}:</div>
                )}
                {this.state.continuitySelected && (
                    <div className="selectedObj" data-testid="selected-continuity" key={this.state.continuitySelected.id}>
                      <div className="selectedMyAlign selObjHigh">
                        <div className="inLineBlk alignOdjLists">
                          <div className="alignOdjtex" id={`selected-continuity-${this.state.continuitySelected.id}`}>{this.state.continuitySelected.goal}</div>
                          <div className="alignObjInfo">
                            {this.state.continuitySelected.team_name && this.state.continuitySelected.team_name != null && (
                              <span>
                                <span>{this.state.continuitySelected.team_name} </span>
                                <span className="dotClass"></span>
                              </span>
                            )}
                            {this.state.continuitySelected.user_firstname && this.state.continuitySelected.user_firstname != null && (
                              <span>
                                {this.state.continuitySelected.user_firstname} {this.state.continuitySelected.user_lastname}{' '}
                              </span>
                            )}
                            <span>
                              {this.state.continuitySelected.user_firstname && this.state.continuitySelected.user_firstname != null && (
                              <span className="dotClass"></span>
                              )}
                              <span>{this.periodNameDisplay(this.state.continuitySelected)}</span>
                            </span>
                          </div>
                        </div>
                        <RemoveIconButton
                          name="Clear"
                          aria-label='Remove'
                          aria-describedby={`selected-continuity-${this.state.continuitySelected.id}`}
                          id={`removeContinuityBtn${this.state.continuitySelected.id}`}
                          onClick={e => this.handleContinuityChange(e, this.state.continuitySelected)}
                        />
                      </div>
                    </div>
                  )}
              </div>
            )}
            {this.state.wsArchiEn && (
              <div>
                <div className="aEObjectiveDep editObjhelpIconPosition">
                  <div className="ObjectiveDepText">{t('Objective has dependencies on')}</div>
                  <div className="editObj-help_tooltip-position">
                  <StyledIconButton
                    aria-describedby='Objective-dependencies-help_tooltip'
                    aria-label={t('Help')}
                    onMouseEnter={event => {
                      this.setState({
                        depedenciesHelpPopUp: !this.state.depedenciesHelpPopUp
                      });
                    }}
                    onMouseLeave={event => {
                      this.setState({
                        depedenciesHelpPopUp: !this.state.depedenciesHelpPopUp
                      });
                    }}
                    onClick={(e) => this.editObjhelpPopperHandler(e, 'depedenciesHelpPopUp', true)}
                    onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'depedenciesHelpPopUp', true)}
                    id="Objective-dependencies-help_tooltip_id"
                  >
                    <HelpOutline />
                  </StyledIconButton>
                  {this.state.depedenciesHelpPopUp && (
                    <div className="form-popup editObjTooltipContentPosition"
                      id="Objective-dependencies-help_tooltip"
                      onMouseEnter={event => { this.setState({ depedenciesHelpPopUp: true }) }}
                      onMouseLeave={event => { this.setState({ depedenciesHelpPopUp: false }) }}
                      onKeyDown={(e) => {
                        this.editObjhelpPopperTabHandler(e, 'depedenciesHelpPopUp', 'Objective-dependencies-help_tooltip_id')
                      }}
                    >
                        {t('If your objective is wholly dependent on another existing objective, you can identify that here.')}
                        <a
                          className="learnmore"
                          target="_blank"
                          href="https://workboard.zendesk.com/hc/en-us/articles/216294188-Goals-and-metrics"
                        >
                          {t('Learn more')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="objDependencies">
                  <div
                    className="objDependSearchdiv"
                    data-testid="searchOkrTeamPerson"
                    onClick={event => this.enableDepdency(event)}
                  >
                    {t('Search for an OKR by person, objective name, or team')}
                  </div>
                  {this.state.dependenciedEn && (
                    <div
                      id="myAlign"
                      className={
                        'dropdown-alignment ' + this.state.setreverseDep
                      }
                    >
                      <input
                        type="text"
                        placeholder={t('Search for an OKR by person, objective name, or team')}
                        className="objDependSearch"
                        id="Dependency"
                        data-testid="testDependSearch"
                        onChange={event =>
                          this.changeSearchBox(event, 'dependency')
                        }
                        autoFocus={true}
                      />
                      <div className="dependenLists">
                        {this.state.filter &&
                          this.state.filter.dependencies &&
                          this.state.filter.dependencies.map(eachVal => (
                            <div className="myAlign" data-testid="myAlign" key={`Depen${eachVal.id}`}>
                              <input
                                id={'Depe_' + eachVal.id}
                                className="defaultUnCheck alignCheck inLineBlk"
                                onChange={e =>
                                  this.handleDependecyChange(e, eachVal)
                                }
                                type="checkbox"
                                defaultChecked={
                                  this.state.dependenciesSelected.find(
                                    val => val.id == eachVal.id
                                  )
                                    ? true
                                    : false
                                }
                              />
                              <label htmlFor={'Depe_' + eachVal.id} />
                              <div className="inLineBlk alignOdjList">
                                <div className="alignOdjtex">
                                  {eachVal.goal}
                                </div>
                                <div className="alignObjInfo">
                                  {eachVal.team_name &&
                                    eachVal.team_name != null && (
                                      <span>
                                        <span>{eachVal.team_name} </span>
                                        <span className="dotClass"></span>
                                      </span>
                                    )}
                                  <span>
                                    {eachVal.user_firstname}{' '}
                                    {eachVal.user_lastname}{' '}
                                  </span>
                                  <span>
                                    <span className="dotClass"></span>
                                    <span>{this.periodNameDisplay(eachVal)}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                {this.state.dependenciesSelected.length > 0 && (
                  <div className="selectedObj">{t('Depends on')}:</div>
                )}
                {this.state.dependenciesSelected.length > 0 &&
                  this.state.dependenciesSelected.map(eachDep => (
                    <div className="selectedObj" data-testid="selected-dependencies" key={eachDep.id}>
                      <div className="selectedMyAlign selObjHigh">
                        <div className="inLineBlk alignOdjLists">
                          <div className="alignOdjtex" id={`selected-dependency-${eachDep.id}`}>{eachDep.goal}</div>
                          <div className="alignObjInfo">
                            {eachDep.team_name && eachDep.team_name != null && (
                              <span>
                                <span>{eachDep.team_name} </span>
                                <span className="dotClass"></span>
                              </span>
                            )}
                            {eachDep.user_firstname && eachDep.user_firstname != null && (
                              <span>
                                {eachDep.user_firstname} {eachDep.user_lastname}{' '}
                              </span>
                            )}
                            <span>
                              {eachDep.user_firstname && eachDep.user_firstname != null && (
                              <span className="dotClass"></span>
                              )}
                              <span>{this.periodNameDisplay(eachDep)}</span>
                            </span>
                          </div>
                        </div>
                        <RemoveIconButton
                          name="Clear"
                          aria-label='Remove'
                          aria-describedby={`selected-dependency-${eachDep.id}`}
                          id={`removeDependencyBtn${eachDep.id}`}
                          onClick={e => this.handleDependecyChange(e, eachDep)}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {!window.wbConfig.isObjectiveWorkstreamSupportHidden && this.state.wsArchiEn && executeModule && (
              <div>
                <div className="aEObjectiveAchi editObjhelpIconPosition">
                  <div className="ObjectiveAchiText">
                  {t('Workstreams supporting this Objective')}
                  </div>
                  <div className="editObj-help_tooltip-position">
                  <StyledIconButton
                    aria-describedby='Workstreams-supporting-help_tooltip'
                    aria-label='Help'
                    onMouseEnter={event => {
                      this.setState({
                        achiHelpPopUp: !this.state.achiHelpPopUp
                      });
                    }}
                    onMouseLeave={event => {
                      this.setState({
                        achiHelpPopUp: !this.state.achiHelpPopUp
                      });
                    }}
                    onClick={(e) => this.editObjhelpPopperHandler(e, 'achiHelpPopUp', true)}
                    onKeyDown={(e) => this.editObjhelpPopperHandler(e, 'achiHelpPopUp', true)}
                    id="Workstreams-supporting-help_tooltip_id"
                  >
                    <HelpOutline />
                  </StyledIconButton>
                  {this.state.achiHelpPopUp && (
                    <div className="form-popup editObjTooltipContentPosition"
                      id="Workstreams-supporting-help_tooltip"
                      onMouseEnter={event => { this.setState({ achiHelpPopUp: true }) }}
                      onMouseLeave={event => { this.setState({ achiHelpPopUp: false }) }}
                      onKeyDown={(e) => {
                        this.editObjhelpPopperTabHandler(e, 'achiHelpPopUp', 'Workstreams-supporting-help_tooltip_id')
                      }}
                    >
                        {t('Link the work that will support achievement of this objective; the connection will be visible from the OKR and from your workstream.')}
                        <a
                          className="learnmore"
                          target="_blank"
                          href="https://workboard.zendesk.com/hc/en-us/articles/216294188-Goals-and-metrics"
                        >
                          {t('Learn more')}
                        </a>
                      </div>
                    )}
                  </div>


                </div>
                {executeModule && <>
                  <div className="objectiveAchi inLineBlk">
                    <div
                      className="objAchievSearchdiv"
                      data-testid="objAchievSearchdiv"
                      onClick={event => {
                        this.setState({ achievingEn: !this.state.achievingEn }, () => {
                          if (this.state.achievingEn) {
                            this.workstremRef.current?.scrollIntoView();
                          }
                        });
                      }}
                    >
                      {t('Search by workstream name or team')}
                  </div>

                    {this.state.achievingEn && (
                      <div id="myAlign" data-testid="myAlign" className="dropdown-alignment ">
                        <input
                          type="text"
                          id="WorkStream"
                          data-testid="workstream-search-bar"
                          placeholder={t('Search for a team or workstream')}
                          className="objAchievSearch"
                          onChange={event =>
                            this.changeSearchBox(event, 'workstream')
                          }
                          autoFocus={true}
                        />
                        <div className="workstreamList">
                          {this.state.filter.WorkStream &&
                            this.state.filter.WorkStream.map(eachMainWS => (
                              <div key={eachMainWS.id}>
                                {eachMainWS.team_name != null && (
                                  <div
                                    className="workStreamTitle truncate"
                                    key={`WS${eachMainWS.team_name}`}
                                  >
                                    {eachMainWS.team_name}
                                  </div>
                                )}

                                {eachMainWS.ws != null &&
                                  eachMainWS.ws &&
                                  eachMainWS.ws.map(eachWS => (
                                    <div className="myAlign" data-testid="myAlign" key={eachWS.id}>
                                      <input
                                        className="defaultUnCheck alignCheck inLineBlk"
                                        data-testid="inLineBlk"
                                        onChange={event =>
                                          this.handleWorkStreamChange(
                                            event,
                                            eachMainWS,
                                            eachWS
                                          )
                                        }
                                        type="checkbox"
                                        defaultChecked={this.state.workStreamSelected.find(
                                          function (val) {
                                            if (
                                              val.team_id == eachMainWS.team_id ||
                                              eachMainWS.team_name ==
                                              'Shared Workstream' ||
                                              eachMainWS.team_name ==
                                              'Personal Workstream'
                                            ) {
                                              return val.ws.find(value =>
                                                value.id == eachWS.id
                                                  ? true
                                                  : false
                                              );
                                            }
                                          }
                                        )}
                                        id={'Achi_' + eachWS.id}
                                      />
                                      <label htmlFor={'Achi_' + eachWS.id} />
                                      <div className="inLineBlk alignOdjList">
                                        <div className="alignObjInfo truncate">
                                          {eachWS.name}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <a id="wrkstrmMy"  ref={this.workstremRef}></a>
                <div className="createWs">
                  <Button
                    className="createWsBtn"
                    kind="normal"
                    onClick={event => {
                      this.setState({ createWsPopUp: !this.state.createWsPopUp });
                    }}
                    variant="ghost"
                    startIcon={<i className="createWsImg"></i>}
                  >
                    {t('Create new workstream')}
                  </Button>
                </div>
                </>}
                {executeModule && this.state.workStreamSelected.length > 0 && (
                  <div className="selectedObj">{`${t('Supporting workstream(s)')}:`}</div>
                )}
                {executeModule && this.state.workStreamSelected.length > 0 &&
                  this.state.workStreamSelected.map(
                    MainWS =>
                      MainWS.ws &&
                      MainWS.ws.map(subWS => (
                        <div className="selectedObj" data-testid="selected-workstream" key={subWS.id}>
                          <div className="selectedMyAlign selObjHigh">
                            <div className="inLineBlk alignOdjLists">
                              <div className="alignOdjtex" id={`selected-workstream-${subWS.id}`}>{subWS.name}</div>
                              <div className="alignObjInfo">
                                <span>{MainWS.team_name}</span>{' '}
                              </div>
                            </div>
                            <RemoveIconButton
                              name="Clear"
                              aria-label={t('Remove')}
                              aria-describedby={`selected-workstream-${subWS.id}`}
                              id={`removeWorkstreamBtn${subWS.id}`}
                              onClick={event =>
                                this.handleWorkStreamChange(
                                  event,
                                  MainWS,
                                  subWS
                                )
                              }
                            />
                          </div>
                        </div>
                      ))
                  )}
              </div>
            )}
          </div>
          {this.state.createWsPopUp && (
            <CreateWsPopUp closeWsPopUp={this.closeWsPopUp} />
          )}
        </StyledModal>
      </ThemeProvider>
    );
  }
}

export default withTranslation()(EditGoal)
